// src/pages/StaffDashboard.js
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { db, storage, auth } from "../firebase";
import {
  collection,
  doc,
  setDoc,
  serverTimestamp,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";

import { ref, uploadBytesResumable, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";

// These are in your project already (per your screenshots)
import { getOrg } from "../utils/tenant";
import { getStaffSession, clearStaffSession, setStaffSession } from "../utils/staffSession";
import { useTheme } from "../theme/ThemeProvider";

import { formatDurationMinutes, formatDurationSeconds } from "../utils/durationFormat";
import {
  captureMorpheusSubmission,
  findMorpheusOccurrence,
  isMorpheusOccurrenceFiled,
  morpheusCountScheduled,
  snapshotSubmissionFields,
} from "../utils/morpheusSubmission";
/* ---------------- per-store collections ---------------- */
const COLL = { drafts: "dashDrafts", submissions: "submissions" };
const CUSTOM_REPORT_ID = "custom_report_fixed";

const tsToDate = (v) =>
  v?.toDate
    ? v.toDate()
    : v?.seconds
    ? new Date(v.seconds * 1000)
    : v
    ? new Date(v)
    : null;

const fmtDateTime = (ts) => {
  const d = tsToDate(ts);
  return d ? d.toLocaleString() : "";
};


const localDayKey = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const fmtTimeOnly = (ts, fallback = "") => {
  const d = tsToDate(ts);
  if (!d) return fallback || "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const normalizedSubheadingTargetKey = (value = "") =>
  String(value || "").trim() || "General";

const normalizedSubheadingCompareKey = (value = "") =>
  normalizedSubheadingTargetKey(value).toLowerCase();

const minutesBetween = (from, to) => Math.round((to.getTime() - from.getTime()) / 60000);

const humanDueLabel = (item, now = new Date()) => {
  const due = tsToDate(item?.dueAt);
  if (!due) return item?.dueTime ? `Due ${item.dueTime}` : "No due time";
  const mins = minutesBetween(now, due);
  if (isDutyCompleted(item)) return `Completed ${fmtTimeOnly(item.completedAt)}`;
  if (mins < 0) return `${formatDurationMinutes(Math.abs(mins))} overdue`;
  if (mins === 0) return "Due now";
  return `Due in ${formatDurationMinutes(mins)}`;
};

const graceEndForDuty = (item) => {
  const explicit = tsToDate(item?.graceEndsAt || item?.escalationAt);
  if (explicit) return explicit;
  const due = tsToDate(item?.dueAt);
  if (!due) return null;
  const mins = Number(item?.gracePeriodMins || item?.escalateAfterMins || 0);
  return mins > 0 ? new Date(due.getTime() + mins * 60000) : due;
};

const isDutyCompleted = (item) => item?.status === "completed" || item?.completed === true;

const dutyTimestampMillis = (value) => {
  if (value == null) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") {
    return value.seconds * 1000 + Number(value.nanoseconds || 0) / 1000000;
  }
  const millis = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(millis) ? millis : 0;
};

// A submitted section starts a fresh form. Its previously completed occurrences
// stay completed in Intelligence/history, but must not lock the empty draft.
const isDutyFiledWithSection = (item, submission) => {
  if (item?.completionSource === "morpheus") return isMorpheusOccurrenceFiled(item, submission);
  if (!isDutyCompleted(item)) return false;
  const submittedAt = dutyTimestampMillis(submission?.at);
  const completedAt = dutyTimestampMillis(item?.completedAt);
  return submittedAt > 0 && completedAt > 0 && completedAt <= submittedAt;
};

const isEscalationActuallyReady = (item, now = new Date()) => {
  if (isDutyCompleted(item)) return false;
  if (!(item?.escalationStatus === "ready" || item?.escalationStatus === "sent")) return false;
  const graceEnd = graceEndForDuty(item);
  return !!graceEnd && now.getTime() > graceEnd.getTime();
};

const reminderAtForDuty = (item) => {
  const explicit = tsToDate(item?.reminderAt);
  if (explicit) return explicit;
  const due = tsToDate(item?.dueAt);
  const mins = Number(item?.remindBeforeMins || 0);
  return due && mins > 0 ? new Date(due.getTime() - mins * 60000) : null;
};

const snoozeOptionsForDuty = (item, now = new Date()) => {
  const graceEnd = graceEndForDuty(item);
  if (!graceEnd) return [];
  return [5, 10, 15, 30].filter((mins) => now.getTime() + mins * 60000 <= graceEnd.getTime());
};

const alertPhaseLabel = (item, now = new Date()) => {
  if (isDutyCompleted(item)) return "Completed";
  if (isEscalationActuallyReady(item, now)) return item?.escalationStatus === "sent" ? "Escalation sent" : "Escalation ready";
  const snoozedUntil = tsToDate(item?.snoozedUntil);
  if (snoozedUntil && snoozedUntil.getTime() > now.getTime()) return `Snoozed until ${fmtTimeOnly(snoozedUntil)}`;
  const graceEnd = graceEndForDuty(item);
  const due = tsToDate(item?.dueAt);
  const reminderAt = reminderAtForDuty(item);
  if (graceEnd && now.getTime() > graceEnd.getTime()) return "Grace ended";
  if (due && now.getTime() >= due.getTime()) return "Time to act";
  if (reminderAt && now.getTime() >= reminderAt.getTime()) return "Upcoming alert";
  return "Scheduled";
};

const whatsappDutyStatus = (item = {}) => {
  const notification = item?.dueNotification || {};
  const status = String(notification.status || "").trim().toLowerCase();
  if (!status) return null;

  const acknowledged = !!notification.acknowledgedAt;
  if (acknowledged) {
    return {
      status,
      acknowledged: true,
      label: "WhatsApp acknowledged",
      tone: "#34d399",
      detail: notification.acknowledgedBy ? `Acknowledged by ${notification.acknowledgedBy}` : "Reminder acknowledged",
    };
  }

  if (status === "sent") {
    return { status, acknowledged: false, label: "WhatsApp sent", tone: "#34d399", detail: "Reminder delivered to your response channel" };
  }
  if (status === "partial") {
    return { status, acknowledged: false, label: "WhatsApp partially sent", tone: "#fbbf24", detail: "Some reminder deliveries need attention" };
  }
  if (status === "failed") {
    return { status, acknowledged: false, label: "WhatsApp failed", tone: "#fb7185", detail: notification.reason || "Neo will retry the reminder" };
  }
  if (status === "waiting_for_responder") {
    return { status, acknowledged: false, label: "Waiting for responder", tone: "#fbbf24", detail: notification.reason || "No verified on-duty responder found yet" };
  }
  if (status === "sending") {
    return { status, acknowledged: false, label: "Sending WhatsApp…", tone: "#7dd3fc", detail: "Neo is routing this reminder" };
  }
  if (status === "prepared") {
    return { status, acknowledged: false, label: "WhatsApp prepared", tone: "#7dd3fc", detail: "Reminder prepared by the response engine" };
  }
  return { status, acknowledged: false, label: `WhatsApp ${status.replaceAll("_", " ")}`, tone: "#94a3b8", detail: "" };
};

const managerEscalationStatus = (item = {}) => {
  const escalation = item?.managerEscalation || {};
  const status = String(escalation.status || "").trim().toLowerCase();
  if (!status) return null;

  if (status === "sent") {
    return {
      label: "Manager notified",
      tone: "#fb7185",
      detail: escalation.managerName
        ? `${escalation.managerName} has been notified`
        : "Management has been notified",
    };
  }
  if (status === "queued") {
    return {
      label: "Manager escalation queued",
      tone: "#fbbf24",
      detail: escalation.managerName
        ? `Routing to ${escalation.managerName}`
        : "Neo is routing this exception to management",
    };
  }
  if (status === "failed") {
    return {
      label: "Manager escalation failed",
      tone: "#fb7185",
      detail: escalation.error || "Neo will retry the management notification",
    };
  }
  if (status === "resolved") {
    return {
      label: "Escalation resolved",
      tone: "#34d399",
      detail: escalation.resolvedBy
        ? `Resolved by ${escalation.resolvedBy}`
        : "This escalation has been resolved",
    };
  }
  return {
    label: `Management ${status.replaceAll("_", " ")}`,
    tone: "#c4b5fd",
    detail: escalation.managerName || "",
  };
};

const deriveDutyState = (item, now = new Date()) => {
  if (isDutyCompleted(item)) return "completed";
  const due = tsToDate(item?.dueAt);
  if (!due) return "upcoming";
  if (due.getTime() > now.getTime()) return "upcoming";

  const graceEnd = graceEndForDuty(item);
  if (isEscalationActuallyReady(item, now)) return "overdue";
  if (graceEnd && now.getTime() > graceEnd.getTime()) return "overdue";

  return "dueNow";
};

function useNeoOperationalSnapshot(orgId, storeId, storeName) {
  const [rows, setRows] = useState([]);
  const [publishedStandards, setPublishedStandards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [readError, setReadError] = useState("");
  const fetchingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!orgId || !storeId || fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading((prev) => prev || rows.length === 0);
    try {
      const functions = getFunctions(undefined, "us-central1");
      const readSnapshot = httpsCallable(functions, "getNeoStaffOperationalSnapshot");
      const result = await readSnapshot({ orgId, storeId, storeName: storeName || "" });
      const data = result?.data || {};
      setRows(Array.isArray(data.queue) ? data.queue : []);
      setPublishedStandards(Array.isArray(data.standards) ? data.standards : []);
      setReadError("");
    } catch (error) {
      console.error("Neo operational snapshot read failed", error);
      setReadError(error?.message || "Neo Intelligence could not load live duty data.");
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  }, [orgId, storeId, storeName, rows.length]);

  useEffect(() => {
    if (!orgId || !storeId) {
      setRows([]);
      setPublishedStandards([]);
      setReadError("");
      return undefined;
    }

    refresh();
    // C3 cost optimisation: the backend queue itself is reconciled on a one-minute
    // cadence, so polling the same operational snapshot every 15 seconds only
    // repeats Firestore reads without providing meaningfully fresher duty state.
    // Keep an immediate load plus focus/visibility refresh, and poll once a minute
    // while this tab is actually visible.
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, 60000);
    const onFocus = () => refresh();
    const onVisibility = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [orgId, storeId, refresh]);

  return { rows, publishedStandards, loading, readError, refresh };
}


const timelineTimestamp = (value) => {
  const d = tsToDate(value);
  return d ? d.getTime() : 0;
};

const buildDutyTimeline = (item = {}) => {
  const events = [];

  const push = (key, label, at, status = "done", detail = "") => {
    if (!at) return;
    events.push({ key, label, at, status, detail });
  };

  push(
    "scheduled",
    "Scheduled",
    item.createdAt || item.publishedAt || item.generatedAt || item.dueAt,
    "done",
    item.dueTime ? `Due ${item.dueTime}` : ""
  );

  push(
    "pre_due_reminder",
    "Pre-due reminder sent",
    item?.preDueNotification?.sentAt,
    "done",
    item?.preDueNotification?.recipientName
      ? `Sent to ${item.preDueNotification.recipientName}`
      : ""
  );

  push(
    "due_reminder",
    "Due reminder sent",
    item?.dueNotification?.sentAt,
    "done",
    item?.dueNotification?.recipientNames?.length
      ? `Sent to ${item.dueNotification.recipientNames.join(", ")}`
      : ""
  );

  push(
    "grace_reminder",
    "Grace reminder sent",
    item?.dueRepeatNotification?.sentAt,
    "done",
    item?.dueRepeatNotification?.lastSentIndex
      ? `Still outstanding · repeat ${item.dueRepeatNotification.lastSentIndex}`
      : "Still outstanding during grace"
  );

  push(
    "acknowledged",
    "Reminder acknowledged",
    item?.dueNotification?.acknowledgedAt,
    "done",
    item?.dueNotification?.acknowledgedBy
      ? `By ${item.dueNotification.acknowledgedBy}`
      : ""
  );

  push(
    "snoozed",
    "Duty snoozed",
    item.lastSnoozedAt,
    "done",
    item.snoozeMins ? `${item.snoozeMins} minutes` : ""
  );

  push(
    "grace_ended",
    "Grace ended · escalation eligible",
    item.graceEndedAt || item.graceEndsAt || item.escalationAt,
    "attention",
    item?.managerEscalation?.deliveryDelayMins != null
      ? `Manager delivery followed ${item.managerEscalation.deliveryDelayMins} min later`
      : ""
  );

  push(
    "escalation_queued",
    "Management escalation queued",
    item?.managerEscalation?.queuedAt || item.escalationReadyAt,
    "attention",
    item?.managerEscalation?.managerName
      ? `Routing to ${item.managerEscalation.managerName}`
      : ""
  );

  push(
    "manager_notified",
    "Manager notified",
    item?.managerEscalation?.sentAt,
    "attention",
    [
      item?.managerEscalation?.managerName || "",
      item?.managerEscalation?.deliveryMethod
        ? `via ${String(item.managerEscalation.deliveryMethod).replace('both', 'Email + WhatsApp')}`
        : "",
    ].filter(Boolean).join(" · ")
  );

  push(
    "completed",
    item.completionStatus === "late" ? "Completed late" : "Completed",
    item.completedAt,
    "success",
    item.completedBy ? `By ${item.completedBy}` : ""
  );

  push(
    "resolved",
    "Escalation resolved",
    item?.managerEscalation?.resolvedAt || item?.dueNotification?.resolvedAt,
    "success",
    item?.managerEscalation?.resolvedBy
      ? `By ${item.managerEscalation.resolvedBy}`
      : item?.dueNotification?.resolvedBy
      ? `By ${item.dueNotification.resolvedBy}`
      : ""
  );

  return events
    .filter((event) => event.at)
    .sort((a, b) => timelineTimestamp(a.at) - timelineTimestamp(b.at));
};

function DutyTimeline({ item }) {
  const events = buildDutyTimeline(item);
  if (!events.length) {
    return (
      <div style={{
        border: "1px dashed rgba(148,163,184,.18)",
        borderRadius: 14,
        padding: 12,
        color: "rgba(226,232,240,.48)",
        fontSize: 11,
        marginTop: 10,
      }}>
        No response timeline yet.
      </div>
    );
  }

  return (
    <div style={{
      marginTop: 12,
      paddingTop: 10,
      borderTop: "1px solid rgba(148,163,184,.12)",
    }}>
      <div style={{
        color: "rgba(226,232,240,.55)",
        fontSize: 10,
        fontWeight: 950,
        textTransform: "uppercase",
        letterSpacing: ".08em",
        marginBottom: 8,
      }}>
        Response Timeline
      </div>

      <div style={{ display: "grid", gap: 0 }}>
        {events.map((event, index) => {
          const tone =
            event.status === "success" ? "#34d399" :
            event.status === "attention" ? "#fb7185" :
            "#38bdf8";

          return (
            <div key={`${event.key}-${index}`} style={{
              display: "grid",
              gridTemplateColumns: "18px minmax(0, 1fr)",
              gap: 8,
              position: "relative",
              paddingBottom: index === events.length - 1 ? 0 : 10,
            }}>
              <div style={{ position: "relative", display: "flex", justifyContent: "center" }}>
                {index !== events.length - 1 ? (
                  <div style={{
                    position: "absolute",
                    top: 10,
                    bottom: -10,
                    width: 1,
                    background: "rgba(148,163,184,.20)",
                  }} />
                ) : null}
                <div style={{
                  width: 9,
                  height: 9,
                  borderRadius: 999,
                  marginTop: 3,
                  background: tone,
                  boxShadow: `0 0 14px ${tone}55`,
                  zIndex: 1,
                }} />
              </div>

              <div style={{ minWidth: 0 }}>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                  flexWrap: "wrap",
                }}>
                  <span style={{ color: "#e2e8f0", fontSize: 11, fontWeight: 900 }}>
                    {event.label}
                  </span>
                  <span style={{ color: "rgba(226,232,240,.44)", fontSize: 10 }}>
                    {fmtTimeOnly(event.at)}
                  </span>
                </div>
                {event.detail ? (
                  <div style={{ color: "rgba(226,232,240,.50)", fontSize: 10, marginTop: 2 }}>
                    {event.detail}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MorpheusCountProgress({item}) {
  const progress = item.morpheusProgress || {};
  return <div style={{marginTop:12,fontSize:12,lineHeight:1.6}}>
    {progress.blockedReason && <div role="alert">Setup needs attention: {progress.blockedReason}</div>}
    {(progress.sections || []).map(section => <div key={section.sectionId}>
      {section.satisfied ? '✓' : '○'} {section.sectionName || section.sectionId}: {section.counted}/{section.total} products counted
    </div>)}
    <div style={{marginTop:6,opacity:0.8}}>Complete in Morpheus. All required sections and products must be counted.</div>
  </div>;
}

function NeoDutyCard({ item, state, onComplete, onOpen, onSnooze, onAcknowledge, completing, now = new Date(), isLight = false }) {
  const [timelineOpen, setTimelineOpen] = useState(false);
  const isDone = state === "completed";
  const isDue = state === "dueNow";
  const isSnoozed = state === "snoozed";
  const isEscalationReady = isEscalationActuallyReady(item, now);
  const snoozeOptions = !isDone && onSnooze ? snoozeOptionsForDuty(item, now) : [];
  const whatsappStatus = whatsappDutyStatus(item);
  const managerStatus = managerEscalationStatus(item);
  const targetType = String(item?.targetType || 'field').toLowerCase();
  const isAggregateTarget = targetType === 'section' || targetType === 'subheading';
  const targetProgress = item?.targetProgress || null;
  const border = isDone
    ? "rgba(34,197,94,.45)"
    : isEscalationReady
    ? "rgba(244,63,94,.72)"
    : isDue
    ? "rgba(56,189,248,.72)"
    : isSnoozed
    ? "rgba(56,189,248,.45)"
    : "rgba(250,204,21,.45)";
  const accent = isDone ? "#22c55e" : isEscalationReady ? "#f43f5e" : isDue ? "#38bdf8" : isSnoozed ? "#38bdf8" : "#facc15";
  return (
    <div
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={() => onOpen?.(item)}
      onMouseEnter={(e) => {
        if (!onOpen) return;
        e.currentTarget.style.boxShadow = "0 0 0 1px rgba(45,212,191,.28), 0 0 28px rgba(45,212,191,.16), 0 16px 38px rgba(0,0,0,.28)";
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.borderColor = "rgba(45,212,191,.52)";
      }}
      onMouseLeave={(e) => {
        if (!onOpen) return;
        e.currentTarget.style.boxShadow = isDue && !isEscalationReady
          ? "0 0 0 1px rgba(56,189,248,.20), 0 0 30px rgba(56,189,248,.16), 0 12px 34px rgba(0,0,0,.22)"
          : "0 12px 34px rgba(0,0,0,.22)";
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.borderColor = border;
      }}
      onFocus={(e) => {
        if (!onOpen) return;
        e.currentTarget.style.boxShadow = "0 0 0 2px rgba(45,212,191,.30), 0 0 30px rgba(45,212,191,.18)";
      }}
      onBlur={(e) => {
        if (!onOpen) return;
        e.currentTarget.style.boxShadow = isDue && !isEscalationReady
          ? "0 0 0 1px rgba(56,189,248,.20), 0 0 30px rgba(56,189,248,.16), 0 12px 34px rgba(0,0,0,.22)"
          : "0 12px 34px rgba(0,0,0,.22)";
      }}
      onKeyDown={(e) => {
        if (!onOpen) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(item);
        }
      }}
      style={{
        border: `1px solid ${border}`,
        borderRadius: 18,
        padding: 14,
        background: isLight ? "linear-gradient(135deg, #ffffff, #f4f8fd)" : "linear-gradient(135deg, rgba(15,23,42,.92), rgba(15,23,42,.70))",
        boxShadow: isDue && !isEscalationReady ? "0 0 0 1px rgba(56,189,248,.20), 0 0 30px rgba(56,189,248,.16), 0 12px 34px rgba(0,0,0,.22)" : "0 12px 34px rgba(0,0,0,.22)",
        cursor: onOpen ? "pointer" : "default",
        transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: isLight ? "#14253a" : "#f8fafc", fontWeight: 900, fontSize: 15, lineHeight: 1.25 }}>
            {item.dutyLabel || item.label || "Untitled duty"}
          </div>
          {isAggregateTarget ? (
            <div style={{ marginTop: 6, display: 'inline-flex', border: '1px solid rgba(45,212,191,.28)', borderRadius: 999, padding: '3px 7px', color: isLight ? '#0f766e' : '#5eead4', background: 'rgba(20,184,166,.09)', fontSize: 9, fontWeight: 950, textTransform: 'uppercase', letterSpacing: '.08em' }}>
              {targetType === 'section' ? 'Section reminder' : 'Sub-heading reminder'}
            </div>
          ) : null}
          <div style={{ color: isLight ? "#617790" : "rgba(226,232,240,.72)", fontSize: 12, marginTop: 5 }}>
            {item.standardName || "Standard"} {item.subheading ? `• ${item.subheading}` : ""}
          </div>
        </div>
        <span
          style={{
            flex: "0 1 auto",
            maxWidth: "100%",
            whiteSpace: "normal",
            textAlign: "center",
            overflowWrap: "anywhere",
            border: `1px solid ${border}`,
            color: accent,
            borderRadius: 999,
            padding: "5px 9px",
            fontSize: 11,
            fontWeight: 900,
            background: isLight ? "rgba(241,246,252,.96)" : "rgba(15,23,42,.84)",
          }}
        >
          {isDone ? (item.completionStatus === "late" ? "Late" : "Done") : isEscalationReady ? "Escalation ready" : isDue ? "Due now" : isSnoozed ? "Snoozed" : "Upcoming"}
        </span>
      </div>

      {isAggregateTarget && targetProgress ? (
        <div style={{ marginTop: 11, border: '1px solid rgba(148,163,184,.14)', borderRadius: 12, padding: '9px 10px', background: isLight ? '#f7fafc' : 'rgba(2,6,23,.34)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 10, fontWeight: 900, color: isLight ? '#334a63' : 'rgba(226,232,240,.72)' }}>
            <span>{targetProgress.done}/{targetProgress.total} complete</span>
            <span>{targetProgress.percent}%</span>
          </div>
          <div style={{ height: 5, borderRadius: 999, background: isLight ? '#dbe7f2' : 'rgba(148,163,184,.14)', overflow: 'hidden', marginTop: 6 }}>
            <div style={{ height: '100%', width: `${targetProgress.percent}%`, background: 'linear-gradient(90deg,#14b8a6,#38bdf8)' }} />
          </div>
          {targetProgress.outstanding?.length ? (
            <div style={{ marginTop: 7, color: isLight ? '#617790' : 'rgba(226,232,240,.58)', fontSize: 10, lineHeight: 1.4 }}>
              Outstanding: {targetProgress.outstanding.slice(0, 3).join(' • ')}{targetProgress.outstanding.length > 3 ? ` +${targetProgress.outstanding.length - 3} more` : ''}
            </div>
          ) : (
            <div style={{ marginTop: 7, color: '#34d399', fontSize: 10, fontWeight: 900 }}>All checklist fields complete.</div>
          )}
        </div>
      ) : null}

      <div style={{ color: accent, fontSize: 11, fontWeight: 950, marginTop: 10, letterSpacing: ".02em" }}>
        🔔 {alertPhaseLabel(item, now)}
      </div>
      {isEscalationReady ? (
        <div style={{ color: isLight ? "#9f1239" : "#fecdd3", fontSize: 11, fontWeight: 900, marginTop: 6 }}>
          {item.completionSource === 'morpheus' ? 'This count is overdue. Complete the required sections in Morpheus to resolve the exception.' : 'This duty is overdue and management escalation is active. Complete it in Standards to resolve the exception.'}
        </div>
      ) : null}

      {whatsappStatus ? (
        <div
          style={{
            marginTop: 9,
            border: `1px solid ${whatsappStatus.tone}33`,
            borderRadius: 12,
            padding: "8px 10px",
            background: isLight ? "rgba(241,246,252,.92)" : "rgba(2,6,23,.38)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ color: whatsappStatus.tone, fontSize: 11, fontWeight: 950 }}>
              {whatsappStatus.label}
            </div>
            {whatsappStatus.detail ? (
              <div style={{ color: isLight ? "#52667d" : "rgba(226,232,240,.56)", fontSize: 10, marginTop: 2 }}>
                {whatsappStatus.detail}
              </div>
            ) : null}
          </div>
          {!isDone && whatsappStatus.status === "sent" && !whatsappStatus.acknowledged && onAcknowledge ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAcknowledge(item);
              }}
              disabled={completing === item.id}
              style={{
                border: "1px solid rgba(52,211,153,.34)",
                borderRadius: 999,
                padding: "6px 9px",
                background: "rgba(16,185,129,.12)",
                color: "#6ee7b7",
                fontSize: 10,
                fontWeight: 950,
                cursor: completing === item.id ? "wait" : "pointer",
              }}
            >
              Acknowledge
            </button>
          ) : null}
        </div>
      ) : null}

      {managerStatus ? (
        <div
          style={{
            marginTop: 9,
            border: `1px solid ${managerStatus.tone}40`,
            borderRadius: 12,
            padding: "9px 10px",
            background: isLight ? "rgba(244,63,94,.05)" : "rgba(76,29,149,.10)",
          }}
        >
          <div style={{ color: managerStatus.tone, fontSize: 11, fontWeight: 950 }}>
            ⚠ {managerStatus.label}
          </div>
          {managerStatus.detail ? (
            <div style={{ color: isLight ? "#52667d" : "rgba(226,232,240,.58)", fontSize: 10, marginTop: 3 }}>
              {managerStatus.detail}
            </div>
          ) : null}
          {item?.managerEscalation?.overdueMins ? (
            <div style={{ color: isLight ? "#9f1239" : "rgba(254,202,202,.72)", fontSize: 10, marginTop: 3 }}>
              {formatDurationMinutes(item.managerEscalation.overdueMins)} overdue when escalated
            </div>
          ) : null}
        </div>
      ) : null}

      {item.completionSource === 'morpheus' && <MorpheusCountProgress item={item} />}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginTop: 12 }}>
        <div style={{ color: isLight ? "#263b53" : "rgba(226,232,240,.82)", fontSize: 12, fontWeight: 800 }}>
          {humanDueLabel(item)} {item.dueTime ? `• ${item.dueTime}` : ""}
          {isDone && item.completedBy ? (
            <span style={{ display: "block", marginTop: 4, color: isLight ? "#52667d" : "rgba(226,232,240,.68)", fontSize: 11, fontWeight: 850 }}>
              Completed by {item.completedBy}
            </span>
          ) : null}
        </div>
        {!isDone && onComplete && item.completionSource !== 'morpheus' ? (
          <button
            type="button"
            onClick={() => onComplete(item)}
            disabled={completing === item.id}
            style={{
              border: "0",
              borderRadius: 999,
              padding: "8px 12px",
              cursor: completing === item.id ? "wait" : "pointer",
              background: "linear-gradient(135deg, #14b8a6, #38bdf8)",
              color: "#03161f",
              fontWeight: 950,
              fontSize: 12,
              boxShadow: "0 10px 24px rgba(20,184,166,.23)",
              opacity: completing === item.id ? 0.65 : 1,
            }}
          >
            {completing === item.id ? "Saving…" : "Complete"}
          </button>
        ) : !isDone ? (
          <div
            style={{
              color: isLight ? "#0369a1" : "#7dd3fc",
              fontSize: 11,
              fontWeight: 950,
              border: "1px solid rgba(56,189,248,.28)",
              borderRadius: 999,
              padding: "6px 9px",
              background: "rgba(14,165,233,.10)",
              whiteSpace: "nowrap",
            }}
          >
            {item.completionSource === 'morpheus' ? 'Complete in Morpheus' : 'Open in Standards →'}
          </div>
        ) : null}
      </div>

            <div style={{ marginTop: 10 }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setTimelineOpen((open) => !open);
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = "0 0 18px rgba(56,189,248,.18)";
            e.currentTarget.style.borderColor = "rgba(56,189,248,.46)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = "none";
            e.currentTarget.style.borderColor = "rgba(148,163,184,.18)";
          }}
          style={{
            width: "100%",
            border: "1px solid rgba(148,163,184,.18)",
            borderRadius: 12,
            padding: "8px 10px",
            background: "rgba(15,23,42,.46)",
            color: "#7dd3fc",
            fontSize: 10,
            fontWeight: 950,
            cursor: "pointer",
            textAlign: "left",
            transition: "box-shadow .18s ease, border-color .18s ease",
          }}
        >
          {timelineOpen ? "Hide response timeline" : "View response timeline"} →
        </button>

        {timelineOpen ? <DutyTimeline item={item} /> : null}
      </div>

      {!isDone && onSnooze ? (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: "rgba(226,232,240,.58)", fontSize: 11, fontWeight: 850, marginBottom: 7 }}>
            Snooze is limited by the grace period.
          </div>
          {snoozeOptions.length ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {snoozeOptions.map((mins) => (
                <button
                  key={mins}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSnooze(item, mins);
                  }}
                  disabled={completing === item.id}
                  style={{
                    border: "1px solid rgba(56,189,248,.35)",
                    borderRadius: 999,
                    padding: "6px 9px",
                    background: "rgba(14,165,233,.12)",
                    color: "#7dd3fc",
                    fontSize: 11,
                    fontWeight: 950,
                    cursor: completing === item.id ? "wait" : "pointer",
                  }}
                >
                  Snooze {mins}m
                </button>
              ))}
            </div>
          ) : (
            <div style={{ color: "rgba(251,113,133,.86)", fontSize: 11, fontWeight: 900 }}>
              Grace window closed — complete in Standards.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}


const dutyVisibleFrom = (item) => {
  if (isDutyCompleted(item)) return null;
  const due = tsToDate(item?.dueAt);
  if (!due) return null;

  const reminder = reminderAtForDuty(item);
  // The engine stores reminderAt = dueAt when no pre-due reminder exists,
  // so this naturally means: reminder configured -> show at reminder;
  // no reminder -> show only when due.
  return reminder || due;
};

const isDutyVisibleInIntelligence = (item, now = new Date()) => {
  if (isDutyCompleted(item)) return true;
  const visibleFrom = dutyVisibleFrom(item);
  return !!visibleFrom && now.getTime() >= visibleFrom.getTime();
};

const dutyBucket = (item, now = new Date()) => {
  if (isDutyCompleted(item)) return "completed";
  const managerStatus = String(item?.managerEscalation?.status || "").toLowerCase();
  if (["queued", "sent", "failed", "waiting_for_manager"].includes(managerStatus)) return "escalated";
  const due = tsToDate(item?.dueAt);
  if (!due) return "upcoming";
  if (due.getTime() > now.getTime()) return "upcoming";
  const graceEnd = graceEndForDuty(item);
  if ((graceEnd && now.getTime() > graceEnd.getTime()) || isEscalationActuallyReady(item, now)) return "overdue";
  return "dueNow";
};

const dutyCountdownLabel = (item, now = new Date()) => {
  const due = tsToDate(item?.dueAt);
  if (!due) return "";
  const diffMs = due.getTime() - now.getTime();
  const absSeconds = Math.floor(Math.abs(diffMs) / 1000);
  const duration = formatDurationSeconds(absSeconds);
  return diffMs >= 0 ? duration : `${duration} overdue`;
};


const clickableGlowHandlers = (active = true) => active ? {
  onMouseEnter: (e) => {
    e.currentTarget.style.transform = "translateY(-2px)";
    e.currentTarget.style.boxShadow = "0 0 0 1px rgba(45,212,191,.24), 0 0 28px rgba(45,212,191,.16), 0 14px 34px rgba(0,0,0,.22)";
    e.currentTarget.style.borderColor = "rgba(45,212,191,.48)";
  },
  onMouseLeave: (e) => {
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = "";
    e.currentTarget.style.borderColor = "";
  },
  onFocus: (e) => {
    e.currentTarget.style.boxShadow = "0 0 0 2px rgba(45,212,191,.28), 0 0 26px rgba(45,212,191,.16)";
  },
  onBlur: (e) => {
    e.currentTarget.style.boxShadow = "";
  },
} : {};

const dutySearchText = (item = {}) => [
  item.dutyLabel,
  item.label,
  item.standardName,
  item.subheading,
  item.storeName,
  item.assignedToName,
  item.managerEscalation?.managerName,
  item.targetType,
  item.targetKey,
  ...(item.targetProgress?.outstanding || []),
].filter(Boolean).join(" ").toLowerCase();

function ExecutionIntelligencePanel({ rows, loading, readError = "", onComplete, onOpenDuty, onSnoozeDuty, onAcknowledgeDuty, completing, themeMode = "dark" }) {
  const isLight = themeMode === "light";
  const [isCompact, setIsCompact] = useState(() => typeof window !== "undefined" ? window.matchMedia("(max-width: 780px)").matches : false);
  const [isNarrowTablet, setIsNarrowTablet] = useState(() => typeof window !== "undefined" ? window.matchMedia("(max-width: 980px)").matches : false);
  const [clock, setClock] = useState(Date.now());
  const [queueFilter, setQueueFilter] = useState("all");
  const [queueSearch, setQueueSearch] = useState("");
  const [expandedColumn, setExpandedColumn] = useState(null);
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const queueColumnsRef = useRef(null);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (
        expandedColumn &&
        queueColumnsRef.current &&
        !queueColumnsRef.current.contains(event.target)
      ) {
        setExpandedColumn(null);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [expandedColumn]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 980px)");
    const sync = () => setIsNarrowTablet(mq.matches);
    sync();
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, []);

  useEffect(() => {
    const t = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  const now = new Date(clock);
  const visibleRows = (rows || []).filter((item) => isDutyVisibleInIntelligence(item, now));

  const groups = visibleRows.reduce(
    (acc, item) => {
      const state = dutyBucket(item, now);
      const bucket = acc[state] ? state : "upcoming";
      acc[bucket].push(item);
      return acc;
    },
    { upcoming: [], dueNow: [], overdue: [], escalated: [], completed: [] }
  );

  const byDueAsc = (a, b) => (tsToDate(a.dueAt)?.getTime?.() || 0) - (tsToDate(b.dueAt)?.getTime?.() || 0);
  const byRecent = (a, b) => (tsToDate(b.completedAt || b.updatedAt)?.getTime?.() || 0) - (tsToDate(a.completedAt || a.updatedAt)?.getTime?.() || 0);
  groups.upcoming.sort(byDueAsc);
  groups.dueNow.sort(byDueAsc);
  groups.overdue.sort(byDueAsc);
  groups.escalated.sort(byDueAsc);
  groups.completed.sort(byRecent);

  const normalizedSearch = queueSearch.trim().toLowerCase();
  const filterRows = (items, state) => items.filter((item) => {
    const matchesFilter =
      queueFilter === "all" ||
      queueFilter === state ||
      (queueFilter === "dueNow" && state === "overdue");
    const matchesSearch = !normalizedSearch || dutySearchText(item).includes(normalizedSearch);
    return matchesFilter && matchesSearch;
  });

  const filtered = {
    upcoming: filterRows(groups.upcoming, "upcoming"),
    dueNow: filterRows(groups.dueNow, "dueNow"),
    overdue: filterRows(groups.overdue, "overdue"),
    escalated: filterRows(groups.escalated, "escalated"),
    completed: filterRows(groups.completed, "completed"),
  };

  const total = visibleRows.length;
  const completed = groups.completed.length;
  const overdue = groups.overdue.length;
  const escalated = groups.escalated.length;
  const dueNow = groups.dueNow.length + groups.overdue.length;
  const onTime = groups.completed.filter((item) => item.completionStatus !== "late").length;
  const onTimePct = completed ? Math.round((onTime / completed) * 100) : 0;

  const metric = (label, value, tone, hint) => (
    <button
      type="button"
      onClick={() => setQueueFilter(label === "Today" || label === "On time" ? "all" : (
        label === "Due now" ? "dueNow" :
        label === "Overdue" ? "overdue" :
        label === "Escalated" ? "escalated" : "all"
      ))}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 0 0 1px rgba(45,212,191,.24), 0 0 24px rgba(45,212,191,.14)";
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.borderColor = "rgba(45,212,191,.44)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.borderColor = "rgba(148,163,184,.18)";
      }}
      onFocus={(e) => {
        e.currentTarget.style.boxShadow = "0 0 0 2px rgba(45,212,191,.28), 0 0 24px rgba(45,212,191,.14)";
      }}
      onBlur={(e) => {
        e.currentTarget.style.boxShadow = "none";
      }}
      style={{
        textAlign: "left",
        border: "1px solid rgba(148,163,184,.18)",
        borderRadius: 18,
        padding: 14,
        background: isLight ? "#ffffff" : "rgba(15,23,42,.72)",
        cursor: "pointer",
        transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
      }}
    >
      <div style={{ color: isLight ? "#617790" : "rgba(226,232,240,.68)", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: ".08em" }}>{label}</div>
      <div style={{ color: tone || (isLight ? "#14253a" : "#f8fafc"), fontSize: 26, fontWeight: 950, marginTop: 4 }}>{value}</div>
      {hint ? <div style={{ color: isLight ? "#7b8da2" : "rgba(226,232,240,.45)", fontSize: 10, marginTop: 3 }}>{hint}</div> : null}
    </button>
  );

  const column = (title, subtitle, items, state, emptyText) => {
    const isExpanded = expandedColumn === state;
    const isHovered = hoveredColumn === state;
    const tone =
      state === "escalated" ? "#fb7185" :
      state === "dueNow" ? "#38bdf8" :
      state === "completed" ? "#34d399" :
      "#facc15";

    return (
      <div
        onMouseEnter={() => setHoveredColumn(state)}
        onMouseLeave={() => setHoveredColumn((current) => current === state ? null : current)}
        style={{
          minWidth: 0,
          border: `1px solid ${
            isExpanded || isHovered ? `${tone}88` : "rgba(148,163,184,.18)"
          }`,
          borderRadius: 18,
          background: isLight ? (isExpanded ? "#ffffff" : isHovered ? "#f7faff" : "#fbfdff") : (isExpanded ? "rgba(15,23,42,.88)" : isHovered ? "rgba(15,23,42,.74)" : "rgba(15,23,42,.58)"),
          boxShadow:
            isExpanded || isHovered
              ? `0 0 26px ${tone}2e, inset 0 0 18px ${tone}0d`
              : "none",
          overflow: "hidden",
          transition: "border-color .18s ease, box-shadow .18s ease, background .18s ease, transform .18s ease",
          transform: isHovered && !isExpanded ? "translateY(-1px)" : "translateY(0)",
        }}
      >
        <button
          type="button"
          onClick={() => setExpandedColumn((current) => current === state ? null : state)}
          aria-expanded={isExpanded}
          style={{
            width: "100%",
            border: 0,
            background: "transparent",
            color: isLight ? "#14253a" : "#f8fafc",
            padding: "14px 15px",
            cursor: "pointer",
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ color: isLight ? "#14253a" : "#f8fafc", fontWeight: 950, fontSize: 15 }}>{title}</span>
              <span
                style={{
                  minWidth: 24,
                  height: 24,
                  padding: "0 7px",
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: tone,
                  background: `${tone}14`,
                  border: `1px solid ${tone}55`,
                  fontSize: 11,
                  fontWeight: 950,
                }}
              >
                {items.length}
              </span>
            </div>
            <div style={{ color: isLight ? "#718399" : "rgba(226,232,240,.52)", fontSize: 11, marginTop: 3 }}>{subtitle}</div>
          </div>

          <span
            aria-hidden="true"
            style={{
              flex: "0 0 auto",
              color: isExpanded ? tone : "rgba(226,232,240,.66)",
              fontSize: 18,
              lineHeight: 1,
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform .18s ease",
            }}
          >
            ▾
          </span>
        </button>

        {isExpanded ? (
          <div
            style={{
              borderTop: "1px solid rgba(148,163,184,.14)",
              padding: "12px",
              display: "grid",
              gap: 10,
              maxHeight: isCompact ? "62vh" : "68vh",
              overflowY: "auto",
              overflowX: "hidden",
              minWidth: 0,
            }}
          >
            {items.slice(0, 12).map((item) => (
              <div key={item.id} style={{ position: "relative", minWidth: 0, width: "100%" }}>
                {!isDutyCompleted(item) && item?.dueAt ? (
                  <div
                    style={{
                      position: "relative",
                      zIndex: 2,
                      display: "block",
                      width: "fit-content",
                      maxWidth: "100%",
                      margin: "0 0 7px auto",
                      color:
                        state === "overdue" || state === "escalated" ? "#fb7185" :
                        state === "dueNow" ? (isLight ? "#0369a1" : "#7dd3fc") : (isLight ? "#334155" : "#cbd5e1"),
                      fontSize: 10,
                      fontWeight: 950,
                      background: isLight ? "#eef4fa" : "rgba(2,6,23,.76)",
                      border: "1px solid rgba(148,163,184,.16)",
                      borderRadius: 999,
                      padding: "4px 7px",
                    }}
                  >
                    {dutyCountdownLabel(item, now)}
                  </div>
                ) : null}

                <NeoDutyCard
                  item={item}
                  state={state === "escalated" ? "dueNow" : state}
                  onComplete={null}
                  onOpen={onOpenDuty}
                  onSnooze={null}
                  onAcknowledge={onAcknowledgeDuty}
                  completing={completing}
                  now={now}
                  isLight={isLight}
                />
              </div>
            ))}

            {items.length === 0 ? (
              <div
                style={{
                  border: "1px dashed rgba(148,163,184,.24)",
                  borderRadius: 14,
                  padding: 14,
                  color: isLight ? "#52667d" : "rgba(226,232,240,.58)",
                  fontSize: 12,
                  background: isLight ? "#f7faff" : "rgba(2,6,23,.30)",
                }}
              >
                {emptyText}
              </div>
            ) : null}

            {items.length > 12 ? (
              <div style={{ color: isLight ? "#617790" : "rgba(226,232,240,.48)", fontSize: 11, textAlign: "center", padding: "4px 0" }}>
                Showing first 12 of {items.length} duties.
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  const filters = [
    ["all", "All"],
    ["upcoming", "Upcoming"],
    ["dueNow", "Due Now"],
    ["escalated", "Escalated"],
    ["completed", "Completed"],
  ];

  return (
    <section
      style={{
        margin: "18px 0 22px",
        border: "1px solid rgba(20,184,166,.18)",
        borderRadius: isCompact ? 20 : 26,
        padding: isCompact ? 12 : 18,
        background: isLight ? "radial-gradient(circle at top left, rgba(42,127,255,.08), transparent 34%), linear-gradient(135deg, #ffffff, #f4f8fd)" : "radial-gradient(circle at top left, rgba(20,184,166,.14), transparent 34%), linear-gradient(135deg, rgba(2,6,23,.88), rgba(15,23,42,.78))",
        boxShadow: "0 24px 60px rgba(0,0,0,.26)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap" }}>
        <div>
          <div style={{ color: "#2dd4bf", fontSize: 11, fontWeight: 950, letterSpacing: ".14em", textTransform: "uppercase" }}>
            Execution Intelligence
          </div>
          <h2 style={{ color: isLight ? "#14253a" : "#f8fafc", fontSize: 24, lineHeight: 1.1, margin: "6px 0 4px", fontWeight: 950 }}>
            Duty Queue
          </h2>
          <p style={{ color: isLight ? "#617790" : "rgba(226,232,240,.66)", margin: 0, fontSize: 13 }}>
            Live operational queue with response, escalation and completion state.
          </p>
        </div>
        <div style={{ color: readError ? "#fb7185" : (loading ? "#d49a00" : "#15995a"), fontSize: 12, fontWeight: 900, padding: "7px 10px", borderRadius: 999, background: isLight ? "rgba(32,183,109,.08)" : "rgba(15,23,42,.78)", border: "1px solid rgba(148,163,184,.18)" }}>
          {readError ? "Read issue" : (loading ? "Syncing…" : "Live")}
        </div>
      </div>

      {readError ? (
        <div style={{
          border: "1px solid rgba(251,113,133,.30)",
          borderRadius: 16,
          padding: 14,
          marginBottom: 14,
          background: "rgba(190,24,93,.08)",
          color: "#fda4af",
          fontSize: 12,
          fontWeight: 800,
        }}>
          Neo could not load the live Duty Queue. {readError}
        </div>
      ) : null}

      {loading && rows.length === 0 ? (
        <div style={{
          border: "1px solid rgba(56,189,248,.18)",
          borderRadius: 16,
          padding: 14,
          marginBottom: 14,
          background: "rgba(2,132,199,.06)",
          color: "#7dd3fc",
          fontSize: 12,
          fontWeight: 800,
        }}>
          Building live duty queue…
        </div>
      ) : null}

      <div style={{
        display: "grid",
        gridTemplateColumns: isCompact ? "repeat(2, minmax(0, 1fr))" : "repeat(auto-fit, minmax(150px, 1fr))",
        gap: isCompact ? 8 : 10,
        marginBottom: 16
      }}>
        {metric("Today", total, "#f8fafc", "All operational duties")}
        {metric("Due now", dueNow, "#38bdf8", "Needs action")}
        {metric("Escalated", escalated, "#f472b6", "Management involved")}
        {metric("On time", `${onTimePct}%`, "#34d399", "Completed on schedule")}
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 16,
          padding: 10,
          borderRadius: 16,
          border: "1px solid rgba(148,163,184,.14)",
          background: isLight ? "rgba(241,246,252,.92)" : "rgba(2,6,23,.32)",
        }}
      >
        <input
          value={queueSearch}
          onChange={(e) => setQueueSearch(e.target.value)}
          placeholder="Search duty, standard, site or owner…"
          style={{
            flex: "1 1 220px",
            minWidth: 0,
            border: "1px solid rgba(148,163,184,.18)",
            borderRadius: 12,
            padding: "9px 11px",
            background: isLight ? "#ffffff" : "rgba(15,23,42,.74)",
            color: isLight ? "#14253a" : "#f8fafc",
            outline: "none",
          }}
        />
        {filters.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setQueueFilter(key)}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 0 16px rgba(45,212,191,.16)";
              e.currentTarget.style.borderColor = "rgba(45,212,191,.42)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "none";
              e.currentTarget.style.borderColor = queueFilter === key
                ? "rgba(45,212,191,.48)"
                : "rgba(148,163,184,.18)";
            }}
            style={{
              border: queueFilter === key ? "1px solid rgba(45,212,191,.48)" : "1px solid rgba(148,163,184,.18)",
              borderRadius: 999,
              padding: "8px 10px",
              background: queueFilter === key ? (isLight ? "rgba(20,184,166,.12)" : "rgba(20,184,166,.14)") : (isLight ? "#ffffff" : "rgba(15,23,42,.58)"),
              color: queueFilter === key ? (isLight ? "#0f8f83" : "#5eead4") : (isLight ? "#536a84" : "rgba(226,232,240,.70)"),
              fontSize: 11,
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {rows.length === 0 && !loading ? (
        <div style={{
          border: "1px dashed rgba(52,211,153,.24)",
          borderRadius: 18,
          padding: 18,
          marginBottom: 14,
          background: "rgba(16,185,129,.05)",
          color: "rgba(226,232,240,.72)",
          fontSize: 13,
        }}>
          <div style={{ color: "#6ee7b7", fontWeight: 950, marginBottom: 4 }}>All clear</div>
          All clear — no live duties match this view.
        </div>
      ) : null}

      <div
        ref={queueColumnsRef}
        style={{
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : isNarrowTablet ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))",
          gap: 10,
          alignItems: "start",
        }}
      >
        {column("Upcoming", "Scheduled later", filtered.upcoming, "upcoming", "No upcoming duties.")}
        {column("Due Now", "Time to act", [...filtered.dueNow, ...filtered.overdue], "dueNow", "No duties due right now.")}
        {column("Escalated", "Management response active", filtered.escalated, "escalated", "No escalated duties.")}
        {column("Completed", "Closed today", filtered.completed, "completed", "Completed duties will appear here.")}
      </div>
    </section>
  );
}

function WorkspaceTabs({ activeTab, onChange, themeMode = "dark" }) {
  const isLight = themeMode === "light";
  const tabBase = {
    border: "1px solid rgba(148,163,184,.18)",
    borderRadius: 999,
    padding: "11px 16px",
    fontSize: 13,
    fontWeight: 950,
    cursor: "pointer",
    transition: "all .18s ease",
  };
  const active = {
    background: "linear-gradient(135deg, #14b8a6, #38bdf8)",
    color: "#03161f",
    boxShadow: "0 14px 30px rgba(20,184,166,.20)",
  };
  const idle = {
    background: isLight ? "#ffffff" : "rgba(15,23,42,.72)",
    color: isLight ? "#334a63" : "rgba(226,232,240,.76)",
    border: isLight ? "1px solid #cbd8e6" : "1px solid rgba(148,163,184,.18)",
  };
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        alignItems: "center",
        margin: "18px auto 10px",
        padding: 8,
        border: "1px solid rgba(148,163,184,.14)",
        borderRadius: 999,
        background: isLight ? "#eef4fa" : "rgba(2,6,23,.46)",
        width: "fit-content",
        maxWidth: "100%",
      }}
    >
      <button
        type="button"
        onClick={() => onChange("intelligence")}
        {...clickableGlowHandlers(true)}
        style={{ ...tabBase, ...(activeTab === "intelligence" ? active : idle) }}
      >
        Intelligence
      </button>
      <button
        type="button"
        onClick={() => onChange("standards")}
        {...clickableGlowHandlers(true)}
        style={{ ...tabBase, ...(activeTab === "standards" ? active : idle) }}
      >
        Standards
      </button>
    </div>
  );
}

/* order sections; pin cash up first */
function sortSections(list) {
  const fixed = list.filter((s) => s.isFixedCashup);
  const rest = list.filter((s) => !s.isFixedCashup);
  const byOrderName = (a, b) => {
    const ao = Number.isFinite(a.order) ? a.order : Number.MAX_SAFE_INTEGER;
    const bo = Number.isFinite(b.order) ? b.order : Number.MAX_SAFE_INTEGER;
    if (ao !== bo) return ao - bo;
    return String(a.name || "").localeCompare(String(b.name || ""));
  };
  fixed.sort(byOrderName);
  rest.sort(byOrderName);
  return [...fixed, ...rest];
}

function isArchivedRecord(item) {
  return item?.archived === true || item?.isArchived === true;
}

function isDeletedRecord(item) {
  return (
    item?.deleted === true ||
    item?.isDeleted === true ||
    item?.removed === true ||
    item?.deletedAt != null ||
    item?.removedAt != null
  );
}

function isVisibleSectionRecord(item) {
  return !isArchivedRecord(item) && !isDeletedRecord(item);
}

function useBuilderSections(orgId, storeId) {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!orgId || !storeId) {
      setList([]);
      return;
    }
    const qy = query(
      collection(db, "orgs", orgId, "staff_dashboard"),
      where("storeId", "==", storeId)
    );
    const unsub = onSnapshot(
      qy,
      (s) => {
        const rows = s.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((row) => isVisibleSectionRecord(row));
        setList(sortSections(rows));
      },
      () => setList([])
    );
    return unsub;
  }, [orgId, storeId]);
  return list;
}


function publishedStandardTargetsStore(standard = {}, storeId = "") {
  const target = String(storeId || "").trim();
  if (!target) return false;

  const direct = String(standard.storeId || "").trim();
  if (direct) return direct === target;

  const ids = Array.isArray(standard.storeIds) ? standard.storeIds : [];
  if (ids.length) {
    return ids.map((v) => String(v || "").trim()).includes(target);
  }

  const stores = Array.isArray(standard.stores) ? standard.stores : [];
  if (stores.length) {
    const matched = stores.some((entry) => {
      if (typeof entry === "string") return String(entry).trim() === target;
      return String(entry?.storeId || entry?.id || "").trim() === target;
    });
    return matched;
  }

  // Truly org-global legacy standards remain supported.
  return true;
}

function usePublishedStandardMetadata(orgId, storeId) {
  const [list, setList] = useState([]);

  useEffect(() => {
    if (!orgId || !storeId) {
      setList([]);
      return undefined;
    }

    const ref = collection(db, "orgs", orgId, "neoStandards");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((row) => {
            const publishState = String(row.publishState || "").toLowerCase();
            const status = String(row.status || "").toLowerCase();
            const isPublished =
              publishState === "published" ||
              status === "published" ||
              row.published === true ||
              row.isPublished === true;
            if (!isPublished) return false;
            if (status === "archived" || row.archived === true || row.isArchived === true) return false;

            // The linked kiosk is a hard store boundary. Do not allow a
            // District Six standard to be merged into Sea Point merely because
            // the section names happen to match.
            return publishedStandardTargetsStore(row, storeId);
          });
        setList(rows);
      },
      () => setList([])
    );

    return unsub;
  }, [orgId, storeId]);

  return list;
}

function mergePublishedStandardMetadata(sections = [], publishedStandards = []) {
  const norm = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const byId = new Map();
  const byName = new Map();

  (publishedStandards || []).filter(standard => standard.completionSource !== 'morpheus').forEach((standard) => {
    [
      standard.staffDashboardSectionId,
      standard.id,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .forEach((key) => byId.set(key, standard));

    const nameKey = norm(standard.name || standard.standardName || standard.sectionName);
    if (nameKey) byName.set(nameKey, standard);
  });

  return (sections || []).map((section) => {
    const sectionId = String(section.id || "").trim();
    const sectionName = norm(section.name || section.sectionName || section.title);
    const published =
      (sectionId && byId.get(sectionId)) ||
      (sectionName && byName.get(sectionName));

    if (!published) return section;

    const publishedDuties = Array.isArray(published.duties) ? published.duties : [];
    const byDutyId = new Map();
    const byLabelSubheading = new Map();
    const byLabel = new Map();

    publishedDuties.forEach((duty) => {
      [
        duty?.dutyId,
        duty?.id,
      ]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
        .forEach((key) => byDutyId.set(key, duty));

      const label = norm(duty?.label || duty?.dutyLabel || duty?.name);
      const subheading = norm(duty?.subheading);
      if (label) {
        byLabel.set(label, duty);
        byLabelSubheading.set(`${subheading}::${label}`, duty);
      }
    });

    const fields = (section.fields || [])
      .map((field) => {
        const dutyId = String(field?.dutyId || field?.id || "").trim();
        const label = norm(field?.label || field?.dutyLabel || field?.name);
        const subheading = norm(field?.subheading);

        const publishedDuty =
          (dutyId && byDutyId.get(dutyId)) ||
          (label && byLabelSubheading.get(`${subheading}::${label}`)) ||
          (label && byLabel.get(label));

        // Once this Standard has a published neoStandards document, that
        // published duty list is authoritative for the Neo App. Duties that
        // still exist in staff_dashboard only because they were deactivated
        // must not remain visible to staff.
        if (!publishedDuty) return null;
        if (publishedDuty.active === false || publishedDuty.disabled === true) return null;

        return {
          ...field,
          ...(publishedDuty.completionSource==='morpheus' ? {type:'morpheus_count',completionSource:'morpheus',morpheusLinkId:publishedDuty.morpheusLinkId,morpheus:publishedDuty.morpheus,dutyId:publishedDuty.dutyId} : {}),
          active: true,
          disabled: false,
          // Staff-facing duty instructions come from the published Standard.
          instructions:
            String(
              publishedDuty.instructions ||
              publishedDuty.instruction ||
              publishedDuty.description ||
              publishedDuty.helpText ||
              publishedDuty.notes ||
              publishedDuty.meta?.instructions ||
              publishedDuty.meta?.description ||
              ""
            ).trim() ||
            String(
              field.instructions ||
              field.instruction ||
              field.description ||
              field.helpText ||
              field.notes ||
              ""
            ).trim(),
        };
      })
      .filter(Boolean);

    return {
      ...section,
      // Standard/section description is independent of whether any duty has an alarm.
      description:
        String(
          published.description ||
          published.standardDescription ||
          published.sectionDescription ||
          published.details ||
          published.notes ||
          published.meta?.description ||
          published.standard?.description ||
          ""
        ).trim() ||
        String(
          section.description ||
          section.standardDescription ||
          section.sectionDescription ||
          section.details ||
          section.notes ||
          ""
        ).trim(),
      fields,
    };
  });
}

/* group fields by subheading */
function groupKeyForIndex(sec, idx) {
  const groups = groupFields(sec || { fields: [] });
  const hit = groups.find((g) => (g.items || []).some((it) => it.idx === idx));
  return hit?.key || "";
}

function groupFields(sec) {
  const map = new Map();
  (sec.fields || []).forEach((f, idx) => {
    const key = (f.subheading || "").trim();
    if (!map.has(key)) map.set(key, []);
    map.get(key).push({ idx, f });
  });
  const groups = Array.from(map.entries()).map(([subheading, items]) => {
    const sorted = items.sort((a, b) => a.idx - b.idx);
    // Stable key for this subheading group (used for per-subheading saves)
    const key = (subheading || "").trim()
      ? `h_${String(subheading).trim()}`
      : `h_blank_${sorted?.[0]?.idx ?? 0}`;
    return {
      key,
      subheading,
      items: sorted,
    };
  });
  return groups.sort((a, b) => (a.items[0]?.idx ?? 0) - (b.items[0]?.idx ?? 0));
}

/* -------- store name binding (optional helper) -------- */
function useBoundStoreName() {
  const read = () =>
    localStorage.getItem("storeName") ||
    localStorage.getItem("storeLabel") ||
    "";
  const [name, setName] = useState(read);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "storeName" || e.key === "storeLabel") setName(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return name;
}

/* ---------------- Photo helpers (MUST be in file scope) ---------------- */
const isMetaObj = (x) => x && typeof x === "object" && !!x.url && !x.kind;
const isFileObj = (x) => typeof File !== "undefined" && x instanceof File;

function makeId() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

/**
 * Photo item (UI model)
 * - local: { kind:'local', id, file, previewUrl, name, type, size, progress, status }
 * - remote: { kind:'remote', id, url, name, type, size, path }
 */
const isLocalPhotoItem = (x) =>
  x && typeof x === "object" && x.kind === "local" && isFileObj(x.file);
const isRemotePhotoItem = (x) =>
  x && typeof x === "object" && x.kind === "remote" && !!x.url;

function toRemoteItem(meta) {
  if (!meta) return null;
  const id = meta.id || makeId();
  return {
    kind: "remote",
    id,
    url: meta.url,
    name: meta.name,
    type: meta.type,
    size: meta.size || 0,
    path: meta.path,
  };
}

function toMeta(remoteItem) {
  if (!remoteItem) return null;
  return {
    url: remoteItem.url,
    name: remoteItem.name,
    type: remoteItem.type,
    size: remoteItem.size || 0,
    path: remoteItem.path,
  };
}


function getPhotoUploadState(value) {
  const items = normalizePhotoValue(value);
  const uploading = items.filter((it) => it?.status === "uploading").length;
  const pending = items.filter((it) => isLocalPhotoItem(it) && it?.status !== "uploading").length;
  const totalActive = uploading + pending;
  return { uploading, pending, totalActive, items };
}

function makeLocalItem(file) {
  return {
    kind: "local",
    id: makeId(),
    file,
    previewUrl: URL.createObjectURL(file),
    name: file?.name || "photo",
    type: file?.type || "image/jpeg",
    size: file?.size || 0,
    progress: 0,
    status: "pending", // pending | uploading | done | error
  };
}

function normalizePhotoValue(v) {
  if (!v) return [];
  // already UI items
  if (Array.isArray(v) && v.length && (isLocalPhotoItem(v[0]) || isRemotePhotoItem(v[0]))) {
    return v;
  }
  if (isLocalPhotoItem(v) || isRemotePhotoItem(v)) return [v];

  // firestore meta(s)
  if (isMetaObj(v)) return [toRemoteItem(v)];
  if (Array.isArray(v) && v.length && v.every(isMetaObj)) return v.map(toRemoteItem);

  // raw files
  if (isFileObj(v)) return [makeLocalItem(v)];
  if (Array.isArray(v)) {
    const files = v.filter(isFileObj);
    if (files.length) return files.map(makeLocalItem);
  }
  return [];
}

/** Ensure we have an authenticated user before Storage operations */
async function ensureStaffAuth() {
  // Your app uses anonymous auth for staff — just ensure it’s active.

  // ✅ On some mobile browsers, Storage can fire before the auth token is fully ready.
  // Wait for currentUser and force-refresh the token once.
  if (!auth?.currentUser) {
    await new Promise((resolve) => {
      if (!auth?.onAuthStateChanged) return resolve();
      const unsub = auth.onAuthStateChanged(() => {
        try { unsub && unsub(); } catch {}
        resolve();
      });
    });
  }
  try {
    if (auth?.currentUser?.getIdToken) {
      await auth.currentUser.getIdToken(true);
    }
  } catch {
    // ignore
  }
}

/** Remove File objects / local items before writing to Firestore */
function stripFilesForFirestore(valuesObj, fields) {
  const out = { ...(valuesObj || {}) };

  Object.keys(out).forEach((k) => {
    const idx = Number(k);
    const f = fields?.[idx];
    const v = out[k];

    if (!f || f.type !== "photo") return;

    // if stored as UI items, only persist remote metas
    if (Array.isArray(v)) {
      const items = v;
      const metas = items
        .filter((it) => isRemotePhotoItem(it) || isMetaObj(it))
        .map((it) => (isMetaObj(it) ? it : toMeta(it)))
        .filter(Boolean);
      out[k] = metas;
      return;
    }

    if (isLocalPhotoItem(v) || isFileObj(v)) {
      out[k] = [];
      return;
    }

    // single remote
    if (isRemotePhotoItem(v)) {
      out[k] = toMeta(v);
      return;
    }
  });

  return out;
}

/**
 * Compress an image File (reduce timeouts).
 * - resizes longest side to maxDim
 * - converts to JPEG (quality)
 * If anything fails, returns the original file.
 */
async function compressImageFile(file, opts = {}) {
  const maxDim = Number(opts.maxDim || 1600);
  const quality = Math.min(0.92, Math.max(0.6, Number(opts.quality || 0.82)));

  try {
    if (!file || !file.type || !file.type.startsWith("image/")) return file;
    if (file.type === "image/gif") return file;

    // Small files: still keep, but we can skip work
    if ((file.size || 0) <= 220 * 1024) return file;

    const dataUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result || ""));
      fr.onerror = () => reject(new Error("file-read-failed"));
      fr.readAsDataURL(file);
    });

    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("image-decode-failed"));
      i.src = dataUrl;
    });

    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return file;

    const scale = Math.min(1, maxDim / Math.max(w, h));
    const tw = Math.max(1, Math.round(w * scale));
    const th = Math.max(1, Math.round(h * scale));

    const canvas = document.createElement("canvas");
    canvas.width = tw;
    canvas.height = th;

    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return file;

    ctx.drawImage(img, 0, 0, tw, th);

    const blob = await new Promise((resolve) => {
      canvas.toBlob(
        (b) => resolve(b),
        "image/jpeg",
        quality
      );
    });

    if (!blob) return file;

    // Keep name but ensure .jpg
    const base = String(file.name || "photo").replace(/\.[^.]+$/, "");
    const newName = `${base}.jpg`;

    return new File([blob], newName, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  }
}

/** Upload a single file -> meta (with optional progress callback) */
async function uploadPhoto(orgId, storeId, sectionId, file, onProgress) {
  if (!file) return null;

  // Retry once if mobile auth token isn't ready yet
  const attempt = async () => {
    await ensureStaffAuth();
  const uid = auth?.currentUser?.uid || "unknown";

  const safeName = String(file?.name || "photo").replace(/[^\w.\-]+/g, "_");

  // Keep this path in-sync with your Storage rules.
  const path = `orgs/${orgId}/stores/${storeId}/reports/${sectionId}/${uid}/${Date.now()}_${safeName}`;

  const r = ref(storage, path);

  const withTimeout = (p, ms = 60000) =>
    Promise.race([
      p,
      new Promise((_, rej) => setTimeout(() => rej(new Error("upload-timeout")), ms)),
    ]);

  // auto-compress to reduce timeouts
  const toUpload = await compressImageFile(file, { maxDim: 1600, quality: 0.82 });

  const metadata = { contentType: toUpload.type || file.type || "image/jpeg" };

  // Try resumable first, fall back to uploadBytes (some localhost setups block resumable)
  let uploadedRef = null;
  try {
    const task = uploadBytesResumable(r, toUpload, metadata);
    await withTimeout(
      new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => {
            if (typeof onProgress === "function" && snap?.totalBytes) {
              const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
              onProgress(pct);
            }
          },
          (err) => reject(err),
          () => resolve()
        );
      }),
      60000
    );
    uploadedRef = task.snapshot.ref;
  } catch (e) {
    // No progress in fallback
    const snap = await withTimeout(uploadBytes(r, toUpload, metadata), 60000);
    uploadedRef = snap.ref;
    if (typeof onProgress === "function") onProgress(100);
  }

    const url = await getDownloadURL(uploadedRef);
    return {
      url,
      name: file.name,
      type: file.type,
      size: file.size || 0,
      path,
    };
  };

  try {
    return await attempt();
  } catch (err) {
    const code = err?.code || err?.message || "";
    const isAuth = String(code).includes("storage/unauthorized") || String(code).includes("storage/unauthenticated");
    if (isAuth) {
      // One more try after re-warming auth/token
      try {
        if (auth?.currentUser?.getIdToken) await auth.currentUser.getIdToken(true);
      } catch {
        // ignore
      }
      return await attempt();
    }
    throw err;
  }
}

/** Converts a value -> meta(s) by uploading if needed; supports UI photo items */
async function uploadValueToMetas(orgId, storeId, sectionId, v, onItemProgress) {
  if (!v) return v;

  // firestore metas already
  if (isMetaObj(v)) return v;
  if (Array.isArray(v) && v.length && v.every(isMetaObj)) return v;

  // UI items
  if (isLocalPhotoItem(v)) {
    const file = v.file;
    const meta = await uploadPhoto(orgId, storeId, sectionId, file, (pct) => {
      if (typeof onItemProgress === "function") onItemProgress(v.id, pct, "uploading");
    });
    if (typeof onItemProgress === "function") onItemProgress(v.id, 100, "done");
    return meta;
  }
  if (isRemotePhotoItem(v)) return toMeta(v);

  // raw file
  if (isFileObj(v)) {
    const meta = await uploadPhoto(orgId, storeId, sectionId, v, null);
    return meta;
  }

  // arrays (ui items or files or metas)
  if (Array.isArray(v)) {
    const items = v;

    // if it's already metas
    if (items.length && items.every(isMetaObj)) return items;

    // if it's ui photo items
    if (items.length && (isLocalPhotoItem(items[0]) || isRemotePhotoItem(items[0]))) {
      const metas = [];
      for (const it of items) {
        if (isRemotePhotoItem(it)) {
          metas.push(toMeta(it));
          continue;
        }
        if (isLocalPhotoItem(it)) {
          const meta = await uploadPhoto(orgId, storeId, sectionId, it.file, (pct) => {
            if (typeof onItemProgress === "function") onItemProgress(it.id, pct, "uploading");
          });
          if (typeof onItemProgress === "function") onItemProgress(it.id, 100, "done");
          metas.push(meta);
        }
      }
      return metas;
    }

    // array of files
    const files = items.filter(isFileObj);
    if (files.length) {
      const metas = await Promise.all(
        files.map((file) => uploadPhoto(orgId, storeId, sectionId, file, null))
      );
      return metas;
    }
  }

  return v;
}

/* ---------------- UI helpers ---------------- */
function TopBar({ onRefresh, onBack, backLabel = "Back", styles }) {
  return (
    <div style={styles.topbar}>
      <button onClick={onRefresh} style={styles.topBtn}>
        ⟳ Refresh
      </button>
      <div style={{ flex: 1 }} />
      <button onClick={onBack} style={styles.topBtn}>
        ← {backLabel}
      </button>
    </div>
  );
}

function Toast({ message, type = "info", onClose, styles }) {
  if (!message) return null;
  const bg =
    type === "error" ? "#d9534f" : type === "success" ? "#28a745" : "#333";
  return (
    <div style={{ ...styles.toast, background: bg }}>
      <span>{message}</span>
      <button onClick={onClose} style={styles.toastX}>
        ×
      </button>
    </div>
  );
}

function ConfirmModal({
  isOpen,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  styles,
}) {
  if (!isOpen) return null;
  return (
    <div style={styles.modalOverlay} role="dialog" aria-modal="true">
      <div style={styles.modal}>
        <div style={styles.modalTitle}>{title}</div>
        <div style={styles.modalMsg}>{message}</div>
        <div style={styles.modalActions}>
          <button
            onClick={onCancel}
            style={{ ...styles.modalBtn, ...styles.modalCancel }}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            style={{ ...styles.modalBtn, ...styles.modalConfirm }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function StaffDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { themeMode } = useTheme();
  const styles = useMemo(() => getStyles(themeMode), [themeMode]);

  const fallbackSession = useMemo(
    () => ({
      storeId: localStorage.getItem("storeId") || "",
      storeName:
        localStorage.getItem("storeName") ||
        localStorage.getItem("storeLabel") ||
        "",
      name:
        localStorage.getItem("staffName") ||
        localStorage.getItem("cashup_staff_name") ||
        "",
    }),
    []
  );
  const incomingSession = location.state?.staffSession || null;
  const session = useMemo(() => {
    if (incomingSession && typeof incomingSession === "object") {
      return setStaffSession(incomingSession) || incomingSession || fallbackSession || null;
    }
    return getStaffSession?.() || fallbackSession || null;
  }, [fallbackSession, incomingSession]);
  const orgObj = useMemo(() => (getOrg?.() ? getOrg() : { orgId: "" }), []);
  const orgId = orgObj?.orgId || localStorage.getItem("orgId") || "";

  const currentBoundStoreId = localStorage.getItem("storeId") || "";
  const sessionStoreId = session?.storeId || "";
  const storeId =
    sessionStoreId && currentBoundStoreId && sessionStoreId !== currentBoundStoreId
      ? currentBoundStoreId
      : sessionStoreId || fallbackSession?.storeId || currentBoundStoreId || "";
  const boundStoreName = useBoundStoreName();
  const storeName =
    boundStoreName ||
    session?.storeName ||
    fallbackSession?.storeName ||
    localStorage.getItem("storeName") ||
    localStorage.getItem("storeLabel") ||
    "Store";
  const staffName = session?.name || fallbackSession?.name || "";
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSessionChecked(true), 250);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!sessionChecked) return;
    if (!orgId || !storeId || !staffName) navigate("/", { replace: true });
  }, [sessionChecked, orgId, storeId, staffName, navigate]);
  useEffect(() => {
    if (!sessionChecked) return;
    if (
      sessionStoreId &&
      currentBoundStoreId &&
      sessionStoreId !== currentBoundStoreId
    ) {
      try { clearStaffSession(); } catch {}
      navigate("/", { replace: true });
    }
  }, [
    sessionChecked,
    sessionStoreId,
    currentBoundStoreId,
    navigate,
  ]);

  // ✅ Eager auth for mobile: make sure anonymous session is established before any uploads
  useEffect(() => {
    (async () => {
      try {
        // warm token
        if (auth?.currentUser?.getIdToken) await auth.currentUser.getIdToken(true);
      } catch {
        // ignore - we'll retry during upload
      }
    })();
  }, []);

  const builderSections = useBuilderSections(orgId, storeId);
  const {
    rows: neoDutyRows,
    publishedStandards: publishedStandardMetadata,
    loading: neoDutyLoading,
    readError: neoDutyReadError,
    refresh: refreshNeoDutyIntelligence,
  } = useNeoOperationalSnapshot(orgId, storeId, storeName);
  const sections = useMemo(
    () => mergePublishedStandardMetadata(builderSections, publishedStandardMetadata),
    [builderSections, publishedStandardMetadata]
  );
  // neoDutyQueue is generated only from published Neo Standards.
  // Do not re-filter it against staff_dashboard drafts: saving an admin draft must never
  // make a currently published Upcoming/Due duty disappear from Intelligence.
  const filteredNeoDutyRows = useMemo(
    () => neoDutyRows || [],
    [neoDutyRows]
  );
  const [standardFocus, setStandardFocus] = useState(null);
  const standardFocusTimerRef = useRef(null);
  const [completingNeoDutyId, setCompletingNeoDutyId] = useState("");
  const aggregateCompletionGuardRef = useRef(new Set());
  const [workspaceTab, setWorkspaceTab] = useState("intelligence");

  useEffect(() => {
    const byId = {};
    const gkByIdx = {};
    const gByKey = {};
    (sections || []).forEach((sec) => {
      byId[sec.id] = sec;
      gkByIdx[sec.id] = {};
      gByKey[sec.id] = {};
      const groups = groupFields(sec);
      groups.forEach((g) => {
        gByKey[sec.id][g.key] = g;
        (g.items || []).forEach((it) => {
          gkByIdx[sec.id][it.idx] = g.key;
        });
      });
    });
    sectionByIdRef.current = byId;
    groupKeyByIdxRef.current = gkByIdx;
    groupByKeyRef.current = gByKey;
  }, [sections]);

  const [formData, setFormData] = useState({});
  const [expandedId, setExpanded] = useState(null);
  const [confirmSec, setConfirm] = useState(null);
  const [lastSubmitted, setLastSubmitted] = useState({});
  const [subSaves, setSubSaves] = useState({});
  const [sectionCompletionStarts, setSectionCompletionStarts] = useState({});
  const sectionCompletionStartsRef = useRef({});
  const [toast, setToast] = useState({ message: "", type: "info" });

  // --- autosave + protect local edits ---
  const autosaveTimersRef = useRef({});
  const lastEditRef = useRef({});
  const sectionByIdRef = useRef({});
  const groupKeyByIdxRef = useRef({});
  const groupByKeyRef = useRef({});
  const saveSubheadingRef = useRef(null);

  const markEdited = (secId, idx) => {
    lastEditRef.current[`${secId}__${idx}`] = Date.now();
  };

  const scheduleAutosave = (secId, groupKey) => {
    const sec = sectionByIdRef.current?.[secId];
    const g = groupByKeyRef.current?.[secId]?.[groupKey];
    if (!sec || !g || !saveSubheadingRef.current) return;

    const k = `${secId}__${groupKey}`;
    if (autosaveTimersRef.current[k]) clearTimeout(autosaveTimersRef.current[k]);
    autosaveTimersRef.current[k] = setTimeout(() => {
      try {
        saveSubheadingRef.current(sec, g);
      } catch {
        // ignore
      }
    }, 350);
  };
  const [submitting, setSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);

  const [customReport, setCustomReport] = useState({ title: "", items: [] });
  const [customSubmitting, setCustomSubmitting] = useState(false);
  const [confirmCustomSubmit, setConfirmCustomSubmit] = useState(false);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    customReportRef.current = customReport;
  }, [customReport]);

  const uploadQueueRef = useRef([]);
  const uploadRunningRef = useRef(false);
  const queuedPhotoIdsRef = useRef(new Set());
  const formDataRef = useRef({});
  const customReportRef = useRef({ title: "", items: [] });

  // Manager-controlled: allow/hide the "Upload" button on staff dashboard
  // Default is ON so existing stores behave the same unless disabled.
  const [staffUploadsEnabled, setStaffUploadsEnabled] = useState(true);

  useEffect(() => {
    if (!orgId || !storeId) {
      setStaffUploadsEnabled(true);
      return;
    }
    const storeRef = doc(db, "orgs", orgId, "stores", storeId);
    const unsub = onSnapshot(
      storeRef,
      (snap) => {
        const v = snap.exists() ? snap.data()?.staffUploadsEnabled : undefined;
        setStaffUploadsEnabled(typeof v === "boolean" ? v : true);
      },
      () => setStaffUploadsEnabled(true)
    );
    return () => unsub();
  }, [orgId, storeId]);

  // Per-subheading UI status (so Save feels instant even with photos)
  // key: `${sec.id}__${group.key}`
  const [subUi, setSubUi] = useState({});

  const syncLocalSubSave = useCallback((secId, groupKey, patch) => {
    setSubSaves((prev) => ({
      ...prev,
      [secId]: {
        ...(prev?.[secId] || {}),
        [groupKey]: {
          ...(prev?.[secId]?.[groupKey] || {}),
          ...patch,
        },
      },
    }));
  }, []);

  const setSubUiState = useCallback((secId, groupKey, patch) => {
    const k = `${secId}__${groupKey}`;
    setSubUi((p) => ({ ...p, [k]: { ...(p[k] || {}), ...patch } }));
  }, []);

  const showToast = useCallback(
    (m, t = "info") => setToast({ message: m, type: t }),
    []
  );

  const ensureSectionCompletionStart = useCallback((secId) => {
    const existing = tsToDate(sectionCompletionStartsRef.current?.[secId]);
    if (existing instanceof Date && !Number.isNaN(existing.getTime())) return existing;
    const startedAt = new Date();
    sectionCompletionStartsRef.current = {
      ...(sectionCompletionStartsRef.current || {}),
      [secId]: startedAt,
    };
    setSectionCompletionStarts((prev) => ({ ...prev, [secId]: startedAt }));
    return startedAt;
  }, []);

  const writeNeoAlert = useCallback(async (item, patch = {}) => {
    if (!orgId || !item?.id) return;
    try {
      await setDoc(doc(collection(db, "orgs", orgId, "neoAlerts")), {
        orgId,
        queueId: item.id,
        standardId: item.standardId || "",
        standardName: item.standardName || "",
        storeId: item.storeId || storeId || "",
        storeName: item.storeName || storeName || "",
        dutyId: item.dutyId || "",
        dutyLabel: item.dutyLabel || item.label || "",
        subheading: item.subheading || "",
        dueTime: item.dueTime || "",
        dayKey: item.dayKey || localDayKey(),
        actorName: staffName || "Unknown",
        actorUid: auth?.currentUser?.uid || "",
        createdAt: serverTimestamp(),
        ...patch,
      });
    } catch (err) {
      console.error("Could not write Neo alert", err);
    }
  }, [orgId, storeId, storeName, staffName]);


  const completeNeoDuty = useCallback(async (item, responseValue = undefined) => {
    if (item?.completionSource === 'morpheus' || item?.morpheusLink) {
      showToast('Complete the required count in Morpheus. Neo will update automatically.', 'warn');
      return false;
    }
    if (!orgId || !item?.id || completingNeoDutyId) return;
    setCompletingNeoDutyId(item.id);
    try {
      const due = tsToDate(item.dueAt);
      const late = !!(due && Date.now() > due.getTime());
      await setDoc(
        doc(db, "orgs", orgId, "neoDutyQueue", item.id),
        {
          status: "completed",
          completed: true,
          completedAt: serverTimestamp(),
          completedBy: staffName || "Unknown",
          completedByUid: auth?.currentUser?.uid || "",
          completionStatus: late ? "late" : "on_time",
          responseValue: responseValue === undefined ? "" : responseValue,
          responseLabel: responseValue === false ? "No" : responseValue === true ? "Yes" : "Completed",
          completedFrom: "standards",
          completedSource: "standards",
          escalationStatus: "",
          escalationReadyAt: null,
          escalationReadyBy: "",
          escalationSentCount: 0,
          dueNotification: {
            // Merge only completion fields; delivery receipts may change while
            // this screen is open and remain owned by the response backend.
            resolved: true,
            resolution: "completed",
            resolvedBy: staffName || "Unknown",
            resolvedByUid: auth?.currentUser?.uid || "",
            resolvedAt: serverTimestamp(),
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      await setDoc(doc(collection(db, "orgs", orgId, "neoTimeline")), {
        orgId,
        type: "duty_completed",
        title: "Duty completed",
        queueId: item.id,
        standardId: item.standardId || "",
        standardName: item.standardName || "",
        storeId: item.storeId || storeId || "",
        storeName: item.storeName || storeName || "",
        dutyId: item.dutyId || "",
        dutyLabel: item.dutyLabel || item.label || "",
        subheading: item.subheading || "",
        completedBy: staffName || "Unknown",
        completedByUid: auth?.currentUser?.uid || "",
        completionStatus: late ? "late" : "on_time",
        completedFrom: "standards",
        createdAt: serverTimestamp(),
      });
      await writeNeoAlert(item, {
        type: "duty_completed",
        title: late ? "Duty completed late" : "Duty completed on time",
        status: "completed",
        completionStatus: late ? "late" : "on_time",
        completedBy: staffName || "Unknown",
        completedAt: serverTimestamp(),
        completedFrom: "standards",
      });
      showToast(late ? "Duty completed late." : "Duty completed on time.", late ? "warn" : "success");
      return true;
    } catch (err) {
      console.error(err);
      showToast("Could not complete duty.", "error");
      return false;
    } finally {
      setCompletingNeoDutyId("");
    }
  }, [orgId, storeId, storeName, staffName, completingNeoDutyId, showToast, writeNeoAlert]);


  const acknowledgeNeoDutyReminder = useCallback(async (item) => {
    if (!orgId || !item?.id || completingNeoDutyId) return;
    const current = item?.dueNotification || {};
    if (current.acknowledgedAt) return;

    setCompletingNeoDutyId(item.id);
    try {
      const acknowledgement = {
        // setDoc(..., { merge: true }) preserves current delivery metadata.
        acknowledgedAt: serverTimestamp(),
        acknowledgedBy: staffName || "Unknown",
        acknowledgedByUid: auth?.currentUser?.uid || "",
        acknowledgedSource: "neo_staff_app",
      };
      await setDoc(
        doc(db, "orgs", orgId, "neoDutyQueue", item.id),
        {
          dueNotification: acknowledgement,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      await setDoc(doc(collection(db, "orgs", orgId, "neoTimeline")), {
        orgId,
        type: "duty_reminder_acknowledged",
        title: "Duty reminder acknowledged",
        queueId: item.id,
        standardId: item.standardId || "",
        standardName: item.standardName || "",
        storeId: item.storeId || storeId || "",
        storeName: item.storeName || storeName || "",
        dutyId: item.dutyId || "",
        dutyLabel: item.dutyLabel || item.label || "",
        acknowledgedBy: staffName || "Unknown",
        acknowledgedByUid: auth?.currentUser?.uid || "",
        channel: "whatsapp",
        createdAt: serverTimestamp(),
      });
      showToast("Reminder acknowledged.", "success");
    } catch (err) {
      console.error(err);
      showToast("Could not acknowledge reminder.", "error");
    } finally {
      setCompletingNeoDutyId("");
    }
  }, [orgId, storeId, storeName, staffName, completingNeoDutyId, showToast]);


  const snoozeNeoDuty = useCallback(async (item, mins) => {
    if (!orgId || !item?.id || completingNeoDutyId) return;
    const options = snoozeOptionsForDuty(item, new Date());
    const chosen = Number(mins || 0);
    if (!options.includes(chosen)) {
      showToast("Snooze cannot go past the allowed grace period.", "warn");
      return;
    }
    const until = new Date(Date.now() + chosen * 60000);
    setCompletingNeoDutyId(item.id);
    try {
      await setDoc(
        doc(db, "orgs", orgId, "neoDutyQueue", item.id),
        {
          status: "snoozed",
          snoozedUntil: until,
          snoozeMins: chosen,
          snoozedBy: staffName || "Unknown",
          snoozedByUid: auth?.currentUser?.uid || "",
          lastSnoozedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      await setDoc(doc(collection(db, "orgs", orgId, "neoTimeline")), {
        orgId,
        type: "duty_snoozed",
        title: "Duty snoozed",
        queueId: item.id,
        standardId: item.standardId || "",
        standardName: item.standardName || "",
        storeId: item.storeId || storeId || "",
        storeName: item.storeName || storeName || "",
        dutyId: item.dutyId || "",
        dutyLabel: item.dutyLabel || item.label || "",
        subheading: item.subheading || "",
        snoozeMins: chosen,
        snoozedUntil: until,
        snoozedBy: staffName || "Unknown",
        snoozedByUid: auth?.currentUser?.uid || "",
        createdAt: serverTimestamp(),
      });
      await writeNeoAlert(item, {
        type: "duty_snoozed",
        title: "Duty snoozed",
        status: "snoozed",
        snoozeMins: chosen,
        snoozedUntil: until,
        snoozedBy: staffName || "Unknown",
      });
      showToast(`Duty snoozed until ${fmtTimeOnly(until)}.`, "success");
    } catch (err) {
      console.error(err);
      showToast("Could not snooze duty.", "error");
    } finally {
      setCompletingNeoDutyId("");
    }
  }, [orgId, storeId, storeName, staffName, completingNeoDutyId, showToast, writeNeoAlert]);

  const reopenNeoDuty = useCallback(async (item) => {
    if (item?.completionSource === 'morpheus' || item?.morpheusLink) {
      showToast('This completion is recorded by Morpheus. Corrections remain in Morpheus.', 'warn');
      return false;
    }
    if (!orgId || !item?.id || completingNeoDutyId) return;
    setCompletingNeoDutyId(item.id);
    try {
      await setDoc(
        doc(db, "orgs", orgId, "neoDutyQueue", item.id),
        {
          status: "pending",
          completed: false,
          completedAt: null,
          completedBy: "",
          completedByUid: "",
          completionStatus: "",
          completedFrom: "",
          completedSource: "",
          escalationStatus: "",
          escalationReadyAt: null,
          escalationReadyBy: "",
          reopenedAt: serverTimestamp(),
          reopenedBy: staffName || "Unknown",
          reopenedByUid: auth?.currentUser?.uid || "",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      await setDoc(doc(collection(db, "orgs", orgId, "neoTimeline")), {
        orgId,
        type: "duty_reopened",
        title: "Duty reopened",
        queueId: item.id,
        standardId: item.standardId || "",
        standardName: item.standardName || "",
        storeId: item.storeId || storeId || "",
        storeName: item.storeName || storeName || "",
        dutyId: item.dutyId || "",
        dutyLabel: item.dutyLabel || item.label || "",
        subheading: item.subheading || "",
        reopenedBy: staffName || "Unknown",
        reopenedByUid: auth?.currentUser?.uid || "",
        reopenedFrom: "standards",
        createdAt: serverTimestamp(),
      });
      await writeNeoAlert(item, {
        type: "duty_reopened",
        title: "Duty reopened",
        status: "pending",
        reopenedBy: staffName || "Unknown",
      });
      showToast("Duty moved back to pending.", "info");
    } catch (err) {
      console.error(err);
      showToast("Could not reopen duty.", "error");
    } finally {
      setCompletingNeoDutyId("");
    }
  }, [orgId, storeId, storeName, staffName, completingNeoDutyId, showToast, writeNeoAlert]);

  useEffect(() => {
    if (!orgId || !Array.isArray(neoDutyRows) || !neoDutyRows.length) return undefined;
    const timer = window.setInterval(() => {
      const now = new Date();
      neoDutyRows.forEach(async (item) => {
        if (!item?.id || isDutyCompleted(item)) return;
        const graceEnd = graceEndForDuty(item);
        const graceHasEnded = !!graceEnd && now.getTime() > graceEnd.getTime();
        if ((item.escalationStatus === "ready" || item.escalationStatus === "sent") && !graceHasEnded) {
          try {
            await setDoc(doc(db, "orgs", orgId, "neoDutyQueue", item.id), {
              escalationStatus: "",
              escalationReadyAt: null,
              escalationReadyBy: "",
              updatedAt: serverTimestamp(),
            }, { merge: true });
          } catch (err) {
            console.error("Could not clear stale escalation", err);
          }
          return;
        }
        if (item.escalationStatus === "ready" || item.escalationStatus === "sent") return;
        if (!graceHasEnded) return;
        try {
          await setDoc(
            doc(db, "orgs", orgId, "neoDutyQueue", item.id),
            {
              status: "overdue",
              escalationStatus: "ready",
              escalationReadyAt: serverTimestamp(),
              escalationReadyBy: "neoSmartAlerts",
              escalationTimelineLogged: true,
              graceEndedAt: graceEnd,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          if (!item.escalationTimelineLogged) {
            await setDoc(doc(collection(db, "orgs", orgId, "neoTimeline")), {
              orgId,
              type: "duty_escalation_ready",
              title: "Duty escalation ready",
              queueId: item.id,
              standardId: item.standardId || "",
              standardName: item.standardName || "",
              storeId: item.storeId || storeId || "",
              storeName: item.storeName || storeName || "",
              dutyId: item.dutyId || "",
              dutyLabel: item.dutyLabel || item.label || "",
              subheading: item.subheading || "",
              dueTime: item.dueTime || "",
              graceEndedAt: graceEnd,
              escalationMethod: item.escalationMethod || "email",
              escalationStatus: "ready",
              createdAt: serverTimestamp(),
            });
          }
          await writeNeoAlert(item, {
            type: "grace_ended",
            title: "Grace window ended",
            status: "overdue",
            escalationStatus: "ready",
            graceEndedAt: graceEnd,
          });
        } catch (err) {
          console.error("Could not mark duty escalation ready", err);
        }
      });
    }, 30000);
    return () => window.clearInterval(timer);
  }, [orgId, neoDutyRows, writeNeoAlert]);

  // Track and cleanup preview URLs (avoid memory leaks)
  const previewUrlsRef = useRef(new Set());
  const trackPreviewUrls = useCallback((items) => {
    (items || []).forEach((it) => {
      if (isLocalPhotoItem(it) && it.previewUrl) previewUrlsRef.current.add(it.previewUrl);
    });
  }, []);
  const revokePreviewUrl = useCallback((url) => {
    if (!url) return;
    try {
      URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
    previewUrlsRef.current.delete(url);
  }, []);
  useEffect(() => {
    return () => {
      // revoke on unmount
      for (const url of previewUrlsRef.current) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore
        }
      }
      previewUrlsRef.current.clear();
    };
  }, []);

  /* init form fields */
  useEffect(() => {
    if (!sections.length) {
      setFormData({});
      return;
    }
    setFormData((prev) => {
      const next = { ...prev };
      sections.forEach((sec) => {
        if (!next[sec.id]) next[sec.id] = {};
        (sec.fields || []).forEach((f, idx) => {
          if (!(idx in next[sec.id])) {
            if (f.type === "checkbox") next[sec.id][idx] = null;
            else if (f.type === "photo") next[sec.id][idx] = [];
            else next[sec.id][idx] = "";
          }
        });
      });
      return next;
    });
  }, [sections]);

  /* live drafts */
  useEffect(() => {
    if (!orgId || !storeId) return;

    const qy = query(
      collection(db, "orgs", orgId, "stores", storeId, COLL.drafts),
      where("storeId", "==", storeId)
    );

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const incoming = {};
        const meta = {};
        const perSub = {};
        const completionStarts = {};
        snap.forEach((d) => {
          const data = d.data() || {};
          const sid = data.sectionId || d.id;
          incoming[sid] = data.values || {};
          meta[sid] = {
            by: data.updatedBy || data.savedBy || "",
            at: data.updatedAt || data.savedAt || null,
          };
          perSub[sid] = data.subheadingSaves || {};
          completionStarts[sid] = data.completionStartedAt || null;
          if (sid === CUSTOM_REPORT_ID && data.customReport) {
            const incomingCustom = {
              title: data.customReport.title || "",
              items: (data.customReport.items || []).map((item) => ({
                ...item,
                value: item.type === "photo" ? normalizePhotoValue(item.value) : item.value,
              })),
            };
            setCustomReport((prev) => {
              if ((prev.items || []).some((it) => it.type === "photo" && normalizePhotoValue(it.value).some(isLocalPhotoItem))) {
                return prev;
              }
              customReportRef.current = incomingCustom;
              return incomingCustom;
            });
          }
        });

        setFormData((prev) => {
          const merged = { ...prev };
          Object.keys(incoming).forEach((sid) => {
            const nextSec = { ...(merged[sid] || {}), ...(incoming[sid] || {}) };
            // Prevent snapshot writes from immediately overwriting very recent local edits
            // (this avoids the "tick then clears" flicker).
            try {
              const prevSec = merged[sid] || {};
              Object.keys(incoming[sid] || {}).forEach((k) => {
                const t = lastEditRef.current[`${sid}__${k}`];
                if (t && Date.now() - t < 2500) {
                  nextSec[k] = prevSec[k];
                }
              });
            } catch {
              // ignore
            }


            // normalize photo fields into UI arrays for thumbnails + per-photo delete
            const sec = sections.find((s) => s.id === sid);
            const fields = sec?.fields || [];
            fields.forEach((f, idx) => {
              const v = nextSec[idx];

              if (f?.type === "photo") {
                const normalized = normalizePhotoValue(v);
                trackPreviewUrls(normalized);
                nextSec[idx] = normalized;
                return;
              }

              if (f?.type === "checkbox") {
                const gk = groupKeyForIndex(sec, idx);
                const savedMeta = perSub?.[sid]?.[gk];
                const hasExplicitSave = !!savedMeta?.saved;
                if (!hasExplicitSave && v === false) {
                  nextSec[idx] = null;
                }
              }
            });

            merged[sid] = nextSec;
          });
          return merged;
        });

        setSubSaves(perSub);
        sectionCompletionStartsRef.current = completionStarts;
        setSectionCompletionStarts(completionStarts);
      },
      (err) => console.error("drafts error:", err)
    );

    return unsub;
  }, [orgId, storeId, sections, trackPreviewUrls]);

    /* live last submitted per section */
  useEffect(() => {
    if (!orgId || !storeId) return;

    const qy = query(
      collection(db, "orgs", orgId, "stores", storeId, COLL.submissions),
      where("storeId", "==", storeId)
    );

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const latest = {};

        snap.forEach((d) => {
          const data = d.data() || {};
          const sid = data.sectionId || d.id;
          const at = data.submittedAt || null;
          const atMs = dutyTimestampMillis(at);

          const prev = latest[sid];
          const prevMs = dutyTimestampMillis(prev?.at);

          if (!prev || atMs > prevMs) {
            latest[sid] = {
              id: d.id,
              by: data.submittedBy || data.staffName || "Unknown",
              at,
              elapsedCompletionSeconds: Number(data.elapsedCompletionSeconds || 0) || 0,
              ...(data.morpheusCompletions ? { morpheusCompletions: data.morpheusCompletions } : {}),
            };
          }
        });

        setLastSubmitted(latest);
      },
      (err) => console.error("submissions error:", err)
    );

    return unsub;
  }, [orgId, storeId]);

  useEffect(() => {
    if (!toast.message) return;
    const t = setTimeout(() => setToast({ message: "", type: "info" }), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleToggle = (secId) =>
    setExpanded((curr) => (curr === secId ? null : secId));

  const handleChange = (secId, idx, type, value) => {
    markEdited(secId, idx);
    ensureSectionCompletionStart(secId);
    const currentValue = formDataRef.current?.[secId]?.[idx];
    const nextValue =
      type === "checkbox"
        ? currentValue === value
          ? null
          : value === true
          ? true
          : value === false
          ? false
          : null
        : value;

    const nextSection = {
      ...(formDataRef.current?.[secId] || {}),
      [idx]: nextValue,
    };
    formDataRef.current = {
      ...(formDataRef.current || {}),
      [secId]: nextSection,
    };

    setFormData((d) => ({
      ...d,
      [secId]: {
        ...(d[secId] || {}),
        [idx]: nextValue,
      },
    }));

    let gk = groupKeyByIdxRef.current?.[secId]?.[idx];
    if (!gk) {
      const sec = sectionByIdRef.current?.[secId];
      const derived = groupFields(sec || {}).find((g) =>
        (g.items || []).some((it) => it.idx === idx)
      );
      gk = derived?.key || "";
    }

    if (gk) {
      const k = `${secId}__${gk}`;
      const cur = subUi?.[k];
      if (!cur || !["saving", "uploading"].includes(cur.state)) {
        setSubUiState(secId, gk, { state: "dirty", msg: "Unsaved changes…", updatedAt: Date.now() });
      }

      const sec = sectionByIdRef.current?.[secId];
      const group = groupByKeyRef.current?.[secId]?.[gk];
      const hasPhoto = (group?.items || []).some((it) => sec?.fields?.[it.idx]?.type === "photo");

      if (!hasPhoto) {
        syncLocalSubSave(secId, gk, {
          saved: true,
          uploadStatus: "done",
          savedBy: staffName || "Unknown",
          savedAt: new Date(),
        });
        setSubUiState(secId, gk, { state: "done", msg: "Saved ✅", updatedAt: Date.now() });
      }

      scheduleAutosave(secId, gk);
    }
  };

  const fieldDone = useCallback((f, v, sectionId = "") => {
    if (!f) return false;
    if(f.completionSource==='morpheus') {
      const row = findMorpheusOccurrence(sectionId, f, neoDutyRows);
      if (row) return isDutyCompleted(row) && !isDutyFiledWithSection(row, lastSubmitted[sectionId]);
      return !morpheusCountScheduled(f);
    }
    // For checkbox duties, either Yes or No is a completed response.
    // Only null/undefined means the duty is not answered and should remain pending.
    if (f.type === "checkbox") return v === true || v === false;
    if (f.type === "photo") {
      const items = Array.isArray(v) ? v : normalizePhotoValue(v);
      return (items || []).length > 0;
    }
    return String(v || "").trim().length > 0;
  }, [neoDutyRows, lastSubmitted]);


  const findAggregateQueueTarget = useCallback(
    (sec, targetType, targetKey = "", options = {}) => {
      if (!sec) return null;
      const type = String(targetType || "").toLowerCase();
      const key = normalizedSubheadingCompareKey(targetKey);
      const includeCompleted = options.includeCompleted === true;
      return (neoDutyRows || []).find((item) => {
        if (!includeCompleted && (item?.status === "completed" || item?.completed)) return false;
        if (isDutyFiledWithSection(item, lastSubmitted[sec.id])) return false;
        const itemSectionId = String(item.staffDashboardSectionId || item.standardId || "");
        if (itemSectionId && itemSectionId !== sec.id) return false;
        if (String(item.targetType || "field").toLowerCase() !== type) return false;
        if (type === "section") return true;
        return normalizedSubheadingCompareKey(item.targetKey || item.subheading || "") === key;
      }) || null;
    },
    [neoDutyRows, lastSubmitted]
  );

  const aggregateProgressForItem = useCallback((item) => {
    const targetType = String(item?.targetType || "field").toLowerCase();
    if (targetType !== "section" && targetType !== "subheading") return null;
    const sectionId = String(item?.staffDashboardSectionId || item?.standardId || "");
    const sec = (sections || []).find((row) => row.id === sectionId);
    if (!sec) return null;

    let candidates = (sec.fields || []).map((f, idx) => ({ f, idx })).filter(({ f }) => f && f.disabled !== true && f.inactive !== true);
    if (targetType === "subheading") {
      const key = normalizedSubheadingCompareKey(item.targetKey || item.subheading || "");
      candidates = candidates.filter(({ f }) => normalizedSubheadingCompareKey(f.subheading || "") === key);
    }

    const outstanding = [];
    let done = 0;
    candidates.forEach(({ f, idx }) => {
      const value = formData?.[sec.id]?.[idx];
      if (fieldDone(f, value, sec.id)) done += 1;
      else outstanding.push(String(f.label || `Duty ${idx + 1}`));
    });
    const total = candidates.length;
    return {
      done,
      total,
      percent: total ? Math.round((done / total) * 100) : 0,
      outstanding,
    };
  }, [sections, formData, fieldDone]);

  const displayNeoDutyRows = useMemo(
    () => (filteredNeoDutyRows || []).map((item) => {
      const progress = aggregateProgressForItem(item);
      return progress ? { ...item, targetProgress: progress } : item;
    }),
    [filteredNeoDutyRows, aggregateProgressForItem]
  );

  const maybeCompleteSubheadingTarget = useCallback(async (sec, group, valuesOverride = null) => {
    if (!sec || !group) return;
    // Resetting the submitted form must never reopen a completed aggregate.
    if ((group.items || []).some(({ f }) => f.completionSource === "morpheus" &&
      isDutyFiledWithSection(findMorpheusOccurrence(sec.id, f, neoDutyRows), lastSubmitted[sec.id]))) return;
    const target = findAggregateQueueTarget(sec, "subheading", group.subheading || "", { includeCompleted: true });
    if (!target) return;
    const values = valuesOverride || formDataRef.current?.[sec.id] || {};
    const activeItems = (group.items || []).filter((it) => it?.f && it.f.disabled !== true && it.f.inactive !== true);
    if (!activeItems.length) return;
    const allComplete = activeItems.every((it) => fieldDone(it.f, values[it.idx], sec.id));

    if (allComplete) {
      if (isDutyCompleted(target) || aggregateCompletionGuardRef.current.has(target.id)) return;
      aggregateCompletionGuardRef.current.add(target.id);
      const ok = await completeNeoDuty(target, `${activeItems.length}/${activeItems.length} fields complete`);
      if (!ok) aggregateCompletionGuardRef.current.delete(target.id);
      return;
    }

    // Completion is evidence-driven. If somebody changes a previously-complete
    // sub-heading back to an incomplete state before section submission, reopen
    // the grouped target instead of leaving Neo with a false "complete" signal.
    if (isDutyCompleted(target) || aggregateCompletionGuardRef.current.has(target.id)) {
      aggregateCompletionGuardRef.current.delete(target.id);
      await reopenNeoDuty(target);
    }
  }, [findAggregateQueueTarget, fieldDone, completeNeoDuty, reopenNeoDuty, neoDutyRows, lastSubmitted]);

  useEffect(()=>{
    for(const sec of sections || []) for(const group of groupFields(sec)) {
      if((group.items || []).some(it=>it.f.completionSource==='morpheus')) maybeCompleteSubheadingTarget(sec,group);
    }
  },[neoDutyRows,sections,maybeCompleteSubheadingTarget]);

  const findNeoQueueForField = useCallback(
    (sec, idx, field, options = {}) => {
      if (!sec || !field) return null;
      if(field.completionSource==='morpheus') return findMorpheusOccurrence(sec.id, field, neoDutyRows);

      const includeCompleted = options.includeCompleted === true;
      const preferCompleted = options.preferCompleted === true;
      const label = String(field.label || "").trim().toLowerCase();
      const fieldDutyId = String(field.dutyId || field.id || "").trim();
      const fieldSubheading = String(field.subheading || "").trim().toLowerCase();

      const matches = (neoDutyRows || []).filter((item) => {
        if (!includeCompleted && (item?.status === "completed" || item?.completed)) return false;
        if (isDutyFiledWithSection(item, lastSubmitted[sec.id])) return false;

        const itemSectionId = String(item.staffDashboardSectionId || item.standardId || "");
        if (itemSectionId && itemSectionId !== sec.id) return false;

        const itemSubheading = String(item.subheading || "").trim().toLowerCase();
        if (itemSubheading !== fieldSubheading) return false;

        const itemDutyId = String(item.dutyId || "").trim();
        const itemLabel = String(item.dutyLabel || item.label || "").trim().toLowerCase();
        const generatedDutyId = `${String(field.label || "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "")}-${idx}`;

        return (
          (fieldDutyId && itemDutyId === fieldDutyId) ||
          (fieldDutyId && itemDutyId.startsWith(`${fieldDutyId}-`)) ||
          (itemDutyId && itemDutyId === generatedDutyId) ||
          (label && itemLabel === label)
        );
      });

      if (!matches.length) return null;

      // Prefer an exact duty id before falling back to a label match.
      const exactIdMatch = fieldDutyId
        ? matches.find((item) => {
            const itemDutyId = String(item.dutyId || "").trim();
            return itemDutyId === fieldDutyId || itemDutyId.startsWith(`${fieldDutyId}-`);
          })
        : null;

      if (exactIdMatch) {
        if (preferCompleted && (exactIdMatch.status === "completed" || exactIdMatch.completed)) {
          return exactIdMatch;
        }
        if (!preferCompleted && !(exactIdMatch.status === "completed" || exactIdMatch.completed)) {
          return exactIdMatch;
        }
      }

      const completed = matches.find((item) => item?.status === "completed" || item?.completed);
      if (preferCompleted && completed) return completed;

      const active = matches.find((item) => !(item?.status === "completed" || item?.completed));
      return active || completed || matches[0];
    },
    [neoDutyRows, lastSubmitted]
  );

  const updateCompletedNeoDutyResponse = useCallback(async (item, responseValue = undefined) => {
    if (item?.completionSource === 'morpheus' || item?.morpheusLink) return false;
    if (!orgId || !item?.id) return;
    try {
      await setDoc(
        doc(db, "orgs", orgId, "neoDutyQueue", item.id),
        {
          responseValue: responseValue === undefined ? "" : responseValue,
          responseLabel: responseValue === false ? "No" : responseValue === true ? "Yes" : "Completed",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (err) {
      console.error(err);
    }
  }, [orgId]);


  const dutyCompletionStateForField = useCallback(
    (sec, idx, field) => {
      const queueItem = findNeoQueueForField(sec, idx, field, {
        includeCompleted: true,
        preferCompleted: true,
      });
      const filed = field.completionSource === "morpheus" && isDutyFiledWithSection(queueItem, lastSubmitted[sec.id]);
      const completed = !filed && !!(queueItem && (queueItem.status === "completed" || queueItem.completed));
      const value = formDataRef.current?.[sec?.id]?.[idx];
      const hasResponse = fieldDone(field, value, sec.id);

      let uploadsPending = false;
      if (field?.type === "photo") {
        const photoState = getPhotoUploadState(value);
        uploadsPending = (photoState?.totalActive || 0) > 0;
      }

      return {
        queueItem,
        filed,
        completed,
        hasResponse,
        uploadsPending,
        canComplete: field.completionSource!=='morpheus' && !!queueItem && !completed && hasResponse && !uploadsPending,
      };
    },
    [fieldDone, findNeoQueueForField, lastSubmitted]
  );

  const markFieldDutyComplete = useCallback(
    async (sec, idx, field) => {
      const state = dutyCompletionStateForField(sec, idx, field);
      if (!state.queueItem) {
        showToast("This field is not linked to a live Neo duty.", "warn");
        return;
      }
      if (state.completed) return;
      if (!state.hasResponse) {
        showToast("Complete the duty response first.", "warn");
        return;
      }
      if (state.uploadsPending) {
        showToast("Wait for the photo upload to finish before completing the duty.", "warn");
        return;
      }
      const value = formDataRef.current?.[sec?.id]?.[idx];
      await completeNeoDuty(state.queueItem, value);
    },
    [completeNeoDuty, dutyCompletionStateForField, showToast]
  );

  const reopenFieldDuty = useCallback(
    async (sec, idx, field) => {
      const state = dutyCompletionStateForField(sec, idx, field);
      if (!state.queueItem || !state.completed) return;
      await reopenNeoDuty(state.queueItem);
    },
    [dutyCompletionStateForField, reopenNeoDuty]
  );

  const openStandardDutyFromIntelligence = useCallback(
    (item) => {
      if (!item) return;
      const targetSectionId = item.staffDashboardSectionId || item.standardId || "";
      const targetDutyId = String(item.dutyId || "");
      const targetLabel = String(item.dutyLabel || item.label || "").trim().toLowerCase();
      const targetType = String(item.targetType || "field").toLowerCase();

      if (targetType === "section" || targetType === "subheading") {
        const targetSec = (sections || []).find((sec) => !targetSectionId || sec.id === targetSectionId);
        setWorkspaceTab("standards");
        if (targetSec?.id) setExpanded(targetSec.id);
        setStandardFocus(targetSec?.id ? { secId: targetSec.id, targetType, targetKey: item.targetKey || item.subheading || "" } : null);
        window.clearTimeout(standardFocusTimerRef.current);
        standardFocusTimerRef.current = window.setTimeout(() => setStandardFocus(null), 3500);

        window.setTimeout(() => {
          const safeSubheading = normalizedSubheadingTargetKey(item.targetKey || item.subheading || "")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");
          const id = targetType === "subheading" && targetSec?.id
            ? `neo-subheading-${targetSec.id}-${safeSubheading || "general"}`
            : targetSec?.id
            ? `neo-section-${targetSec.id}`
            : "neo-standards-workspace";
          document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 120);
        return;
      }

      let target = null;
      for (const sec of sections || []) {
        if (targetSectionId && sec.id !== targetSectionId) continue;
        const fields = sec.fields || [];
        for (let idx = 0; idx < fields.length; idx += 1) {
          const field = fields[idx] || {};
          const fieldDutyId = String(field.dutyId || field.id || "");
          const fieldLabel = String(field.label || "").trim().toLowerCase();
          const matches =
            (fieldDutyId && targetDutyId === fieldDutyId) ||
            (fieldDutyId && targetDutyId.startsWith(`${fieldDutyId}-`)) ||
            (targetDutyId && targetDutyId === `${String(field.label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}-${idx}`) ||
            (targetLabel && fieldLabel === targetLabel);
          if (matches) {
            target = { secId: sec.id, idx };
            break;
          }
        }
        if (target) break;
      }

      setWorkspaceTab("standards");
      if (target?.secId) setExpanded(target.secId);
      setStandardFocus(target);
      window.clearTimeout(standardFocusTimerRef.current);
      standardFocusTimerRef.current = window.setTimeout(() => setStandardFocus(null), 3500);

      window.setTimeout(() => {
        const id = target ? `neo-duty-field-${target.secId}-${target.idx}` : "neo-standards-workspace";
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 120);
    },
    [sections]
  );

  const pill = useMemo(() => {
    const entries = Object.values(subUi || {}).filter(Boolean);
    const states = entries.map((x) => x?.state).filter(Boolean);
    if (states.some((s) => s === "saving")) return { text: "Saving…", kind: "saving" };
    if (states.some((s) => s === "uploading"))
      return { text: "Uploading photos…", kind: "saving" };
    if (states.some((s) => s === "error")) return { text: "Attention needed", kind: "error" };
    const nowTs = Date.now();
    const hasFreshDirty = entries.some((x) => x?.state === "dirty" && nowTs - Number(x?.updatedAt || 0) < 2500);
    if (hasFreshDirty) return { text: "Unsaved changes…", kind: "warn" };
    return { text: "All changes saved", kind: "ok" };
  }, [subUi]);
  const updatePhotoItem = useCallback((secId, idx, photoId, patch) => {
    setFormData((d) => {
      const sec = { ...(d[secId] || {}) };
      const list = Array.isArray(sec[idx]) ? [...sec[idx]] : normalizePhotoValue(sec[idx]);
      const next = list.map((it) => (it?.id === photoId ? { ...it, ...patch } : it));
      sec[idx] = next;
      return { ...d, [secId]: sec };
    });
  }, []);

  const removePhotoItem = useCallback(
    (secId, idx, photoId) => {
      setFormData((d) => {
        const sec = { ...(d[secId] || {}) };
        const list = Array.isArray(sec[idx]) ? [...sec[idx]] : normalizePhotoValue(sec[idx]);
        const toRemove = list.find((it) => it?.id === photoId);
        if (toRemove?.previewUrl) revokePreviewUrl(toRemove.previewUrl);
        sec[idx] = list.filter((it) => it?.id !== photoId);
        return { ...d, [secId]: sec };
      });
    },
    [revokePreviewUrl]
  );

  const clearPhotos = useCallback(
    (secId, idx) => {
      setFormData((d) => {
        const sec = { ...(d[secId] || {}) };
        const list = Array.isArray(sec[idx]) ? sec[idx] : normalizePhotoValue(sec[idx]);
        (list || []).forEach((it) => {
          if (it?.previewUrl) revokePreviewUrl(it.previewUrl);
        });
        sec[idx] = [];
        return { ...d, [secId]: sec };
      });
    },
    [revokePreviewUrl]
  );

  const trySubmit = (sec) => {
    try {
      captureMorpheusSubmission(sec, neoDutyRows, lastSubmitted[sec.id]);
    } catch (error) {
      showToast(error.message, "warn");
      return;
    }
    const groups = groupFields(sec);
    const saves = subSaves?.[sec.id] || {};
    const allSaved = groups.every((g) => (g.items || []).some(it=>it.f.completionSource!=='morpheus') ? !!saves?.[g.key]?.saved : (g.items || []).every(it=>fieldDone(it.f,null,sec.id)));
    if (!allSaved) {
      showToast(
        "Please save every subheading first before submitting this section.",
        "error"
      );
      return;
    }
    setConfirm(sec.id);
  };

  const doSubmit = async (sec) => {
    if (submitting || submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setSubmitting(true);

    try {

      // upload photos first
      const fields = sec.fields || [];
      const morpheusCompletions = captureMorpheusSubmission(sec, neoDutyRows, lastSubmitted[sec.id]);
      const values = { ...(formData[sec.id] || {}) };
      fields.forEach((f,i)=>{if(f.completionSource==='morpheus')delete values[i];});

      for (let i = 0; i < fields.length; i++) {
        const f = fields[i];
        const v = values[i];

        if (f?.type !== "photo" || !v || (Array.isArray(v) && v.length === 0)) continue;

        // normalize for safety
        const items = Array.isArray(v) ? v : normalizePhotoValue(v);
        if (!items.length) continue;

        // upload any local items and convert to metas array
        try {
          // mark as uploading
          items.forEach((it) => {
            if (isLocalPhotoItem(it)) updatePhotoItem(sec.id, i, it.id, { status: "uploading", progress: 0 });
          });

          const metas = await uploadValueToMetas(orgId, storeId, sec.id, items, (photoId, pct) => {
            updatePhotoItem(sec.id, i, photoId, { progress: pct, status: pct >= 100 ? "done" : "uploading" });
          });

          // store as metas for Firestore submit
          values[i] = Array.isArray(metas) ? metas : metas ? [metas] : [];
        } catch (err) {
          let msg = "One of the photos failed to upload.";
          if (err?.message === "upload-timeout")
            msg = "Upload timed out. Try again (or use a smaller photo).";
          else if (err?.code) {
              if (String(err.code).includes("storage/unauthorized")) msg = "Upload blocked (storage/unauthorized). This usually means the staff device is not signed in (anonymous auth) or App Check is enforced. Please go back to PIN/login then try again.";
              else msg = `Upload error (${err.code}).`;
            }
          showToast(msg, "error");
          setConfirm(null);
          setSubmitting(false);
          return;
        }
      }

      const sectionSaveMeta = Object.values(subSaves?.[sec.id] || {});
      const startedDates = sectionSaveMeta
        .map((meta) => tsToDate(meta?.savedAt))
        .filter((date) => date instanceof Date && !Number.isNaN(date.getTime()));
      const persistedStart = tsToDate(sectionCompletionStartsRef.current?.[sec.id] || sectionCompletionStarts?.[sec.id]);
      const completionStartedAt = persistedStart || (startedDates.length
        ? new Date(Math.min(...startedDates.map((date) => date.getTime())))
        : null);
      const completionFinishedAt = new Date();
      const elapsedCompletionSeconds = completionStartedAt
        ? Math.max(0, Math.round((completionFinishedAt.getTime() - completionStartedAt.getTime()) / 1000))
        : null;
      const submissionId = `${sec.id}_${Date.now()}`;

      await setDoc(
        doc(
          db,
          "orgs",
          orgId,
          "stores",
          storeId,
          COLL.submissions,
          submissionId
        ),
        {
          orgId,
          storeId,
          sectionName: sec.name,
          sectionId: sec.id,
          submittedBy: staffName || "Unknown",
          submittedAt: serverTimestamp(),
          subheadingSaves: subSaves?.[sec.id] || {},
          submissionFields: snapshotSubmissionFields(fields),
          morpheusCompletions,
          values,
          ...(completionStartedAt ? { completionStartedAt } : {}),
          ...(elapsedCompletionSeconds != null ? { elapsedCompletionSeconds } : {}),
        }
      );

      // File the source occurrence as soon as its report is committed, before
      // awaiting any draft cleanup. The canonical count remains completed.
      setLastSubmitted((prev) => ({
        ...prev,
        [sec.id]: {
          id: submissionId, by: staffName || "Unknown",
          at: prev[sec.id]?.id === submissionId && dutyTimestampMillis(prev[sec.id]?.at) > 0
            ? prev[sec.id].at : completionFinishedAt,
          elapsedCompletionSeconds: elapsedCompletionSeconds || 0,
          morpheusCompletions,
        },
      }));

      const sectionTarget = findAggregateQueueTarget(sec, "section");
      if (sectionTarget && !aggregateCompletionGuardRef.current.has(sectionTarget.id)) {
        aggregateCompletionGuardRef.current.add(sectionTarget.id);
        const targetCompleted = await completeNeoDuty(sectionTarget, "Section submitted");
        if (!targetCompleted) aggregateCompletionGuardRef.current.delete(sectionTarget.id);
      }

      // clear local values + shared draft
      const cleared = {};
      fields.forEach((f, i) => {
        cleared[i] = f.type === "checkbox" ? null : f.type === "photo" ? [] : "";
      });

      // cleanup preview urls for this section
      try {
        Object.values(formData?.[sec.id] || {}).forEach((val) => {
          const items = normalizePhotoValue(val);
          items.forEach((it) => {
            if (it?.previewUrl) revokePreviewUrl(it.previewUrl);
          });
        });
      } catch {
        // ignore
      }

      // Stop delayed saves from copying the submitted values into the fresh form.
      Object.keys(autosaveTimersRef.current).forEach((key) => {
        if (!key.startsWith(`${sec.id}__`)) return;
        clearTimeout(autosaveTimersRef.current[key]);
        delete autosaveTimersRef.current[key];
      });
      Object.keys(lastEditRef.current).forEach((key) => {
        if (key.startsWith(`${sec.id}__`)) delete lastEditRef.current[key];
      });
      formDataRef.current = { ...(formDataRef.current || {}), [sec.id]: cleared };
      setFormData((d) => ({ ...d, [sec.id]: cleared }));

      await setDoc(
        doc(db, "orgs", orgId, "stores", storeId, COLL.drafts, `${sec.id}`),
        {
          orgId,
          storeId,
          sectionId: sec.id,
          sectionName: sec.name,
          values: stripFilesForFirestore(cleared, fields),
          subheadingSaves: {},
          completionStartedAt: null,
          updatedBy: staffName || "Unknown",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setSubSaves((prev) => ({ ...prev, [sec.id]: {} }));
      setSubUi((prev) => Object.fromEntries(
        Object.entries(prev).filter(([key]) => !key.startsWith(`${sec.id}__`))
      ));
      sectionCompletionStartsRef.current = {
        ...(sectionCompletionStartsRef.current || {}),
        [sec.id]: null,
      };
      setSectionCompletionStarts((prev) => ({ ...prev, [sec.id]: null }));
      setConfirm(null);
      showToast(`✔️ Submitted “${sec.name}” successfully!`, "success");
    } catch (e) {
      showToast(e?.message || "Submit failed.", "error");
      setConfirm(null);
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const saveSubheading = async (sec, group) => {
    const fields = sec.fields || [];
    const valuesAll = { ...(formDataRef.current?.[sec.id] || formData[sec.id] || {}) };
    const completionStartedAt = ensureSectionCompletionStart(sec.id);

    try {
      // ✅ 1) Instant save: write draft immediately (NO photo uploads)
      setSubUiState(sec.id, group.key, { state: "saving", msg: "Saving..." });

      // Write a Firestore-safe version (no File objects / no local items)
      const safeValues = stripFilesForFirestore(valuesAll, fields);

      const existing = subSaves?.[sec.id] || {};

      const uploadTargets = (group.items || [])
        .map((it) => it.idx)
        .filter((idx) => {
          if (fields?.[idx]?.type !== "photo") return false;
          const items = normalizePhotoValue(valuesAll[idx]);
          return items.some((photo) => isLocalPhotoItem(photo));
        });

      if (!uploadTargets.length) {
        syncLocalSubSave(sec.id, group.key, {
          saved: true,
          uploadStatus: "done",
          savedBy: staffName || "Unknown",
          savedAt: new Date(),
        });
        setSubUiState(sec.id, group.key, { state: "done", msg: "Saved ✅", updatedAt: Date.now() });
        await setDoc(
          doc(db, "orgs", orgId, "stores", storeId, COLL.drafts, `${sec.id}`),
          {
            orgId,
            storeId,
            sectionId: sec.id,
            sectionName: sec.name,
            completionStartedAt,
            values: safeValues,
            subheadingSaves: {
              ...existing,
              [group.key]: {
                saved: true,
                uploadStatus: "done",
                savedBy: staffName || "Unknown",
                savedAt: serverTimestamp(),
              },
            },
            updatedBy: staffName || "Unknown",
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        setSubUiState(sec.id, group.key, { state: "done", msg: "Saved ✅" });
        await maybeCompleteSubheadingTarget(sec, group, valuesAll);
        return;
      }

      // mark as saved=false while uploads run
      syncLocalSubSave(sec.id, group.key, {
        saved: false,
        uploadStatus: "uploading",
        savedBy: staffName || "Unknown",
        savedAt: new Date(),
      });
      await setDoc(
        doc(db, "orgs", orgId, "stores", storeId, COLL.drafts, `${sec.id}`),
        {
          orgId,
          storeId,
          sectionId: sec.id,
          sectionName: sec.name,
          completionStartedAt,
          values: safeValues,
          subheadingSaves: {
            ...existing,
            [group.key]: {
              saved: false,
              uploadStatus: "uploading",
              savedBy: staffName || "Unknown",
              savedAt: serverTimestamp(),
            },
          },
          updatedBy: staffName || "Unknown",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setSubUiState(sec.id, group.key, {
        state: "uploading",
        msg: "Uploading photos…",
        updatedAt: Date.now(),
      });

      // ✅ 2) Background upload: upload only this subheading's photo fields
      void (async () => {
        try {
          const nextValues = { ...valuesAll };

          for (const idx of uploadTargets) {
            const v = nextValues[idx];
            const items = Array.isArray(v) ? v : normalizePhotoValue(v);
            if (!items.length) continue;

            // mark local ones as uploading
            items.forEach((it) => {
              if (isLocalPhotoItem(it)) updatePhotoItem(sec.id, idx, it.id, { status: "uploading", progress: 0 });
            });

            try {
              const metas = await uploadValueToMetas(
                orgId,
                storeId,
                sec.id,
                items,
                (photoId, pct) => {
                  updatePhotoItem(sec.id, idx, photoId, {
                    progress: pct,
                    status: pct >= 100 ? "done" : "uploading",
                  });
                }
              );

              // After upload, store as remote items for UI (thumbnails stay)
              const remoteItems = (Array.isArray(metas) ? metas : metas ? [metas] : [])
                .filter(Boolean)
                .map((m) => toRemoteItem(m))
                .filter(Boolean);

              nextValues[idx] = remoteItems;
            } catch (err) {
              console.error("photo upload failed", err);
              let msg = "One of the photos failed to upload.";
              if (err?.message === "upload-timeout")
                msg = "Upload timed out. Try again (or use a smaller photo).";
              else if (err?.code) {
              if (String(err.code).includes("storage/unauthorized")) msg = "Upload blocked (storage/unauthorized). This usually means the staff device is not signed in (anonymous auth) or App Check is enforced. Please go back to PIN/login then try again.";
              else msg = `Upload error (${err.code}).`;
            }

              setSubUiState(sec.id, group.key, { state: "error", msg });
              showToast(msg, "error");
              return;
            }
          }

          // ✅ 3) Patch Firestore with uploaded metas + mark saved true
          // Merge ONLY the finished photo field(s) into the latest section state
          // so rapid checklist taps are never overwritten by an older upload snapshot.
          const latestSectionValues = {
            ...(formDataRef.current?.[sec.id] || {}),
          };

          uploadTargets.forEach((photoIdx) => {
            latestSectionValues[photoIdx] = nextValues[photoIdx];
          });

          formDataRef.current = {
            ...(formDataRef.current || {}),
            [sec.id]: latestSectionValues,
          };

          setFormData((d) => ({
            ...d,
            [sec.id]: {
              ...(d[sec.id] || {}),
              ...uploadTargets.reduce((acc, photoIdx) => {
                acc[photoIdx] = nextValues[photoIdx];
                return acc;
              }, {}),
            },
          }));

          const finalValuesSafe = stripFilesForFirestore(latestSectionValues, fields);

          const existing2 = subSaves?.[sec.id] || {};
          syncLocalSubSave(sec.id, group.key, {
            saved: true,
            uploadStatus: "done",
            savedBy: staffName || "Unknown",
            savedAt: new Date(),
          });
          await setDoc(
            doc(db, "orgs", orgId, "stores", storeId, COLL.drafts, `${sec.id}`),
            {
              values: finalValuesSafe,
              subheadingSaves: {
                ...existing2,
                [group.key]: {
                  saved: true,
                  uploadStatus: "done",
                  savedBy: staffName || "Unknown",
                  savedAt: serverTimestamp(),
                },
              },
              updatedBy: staffName || "Unknown",
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );

          setSubUiState(sec.id, group.key, { state: "done", msg: "Saved ✅", updatedAt: Date.now() });
          await maybeCompleteSubheadingTarget(sec, group, latestSectionValues);
          showToast(`Saved “${group.subheading || "Subheading"}”.`, "success");
        } catch (e) {
          console.error(e);
          setSubUiState(sec.id, group.key, {
            state: "error",
            msg: "Background upload failed.",
          });
          showToast("Background upload failed.", "error");
        }
      })();
    } catch (e) {
      console.error(e);
      setSubUiState(sec.id, group.key, { state: "error", msg: "Save failed.", updatedAt: Date.now() });
      showToast("Could not save (permissions?).", "error");
    }
  };


  const patchDraftAfterPhotoUpload = useCallback(
    async (secId, nextValues) => {
      const sec = sections.find((s) => s.id === secId);
      if (!sec) return;
      const fields = sec.fields || [];
      await setDoc(
        doc(db, "orgs", orgId, "stores", storeId, COLL.drafts, `${secId}`),
        {
          orgId,
          storeId,
          sectionId: secId,
          sectionName: sec.name,
          values: stripFilesForFirestore(nextValues, fields),
          subheadingSaves: subSaves?.[secId] || {},
          updatedBy: staffName || "Unknown",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    },
    [orgId, storeId, sections, staffName, subSaves]
  );

  const patchCustomDraft = useCallback(
    async (nextCustom) => {
      await setDoc(
        doc(db, "orgs", orgId, "stores", storeId, COLL.drafts, CUSTOM_REPORT_ID),
        {
          orgId,
          storeId,
          sectionId: CUSTOM_REPORT_ID,
          sectionName: "Custom Report",
          customReport: {
            title: nextCustom.title || "",
            items: (nextCustom.items || []).map((item) => ({
              ...item,
              value:
                item.type === "photo"
                  ? normalizePhotoValue(item.value).filter((x) => isRemotePhotoItem(x)).map((x) => toMeta(x))
                  : item.value,
            })),
          },
          updatedBy: staffName || "Unknown",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    },
    [orgId, storeId, staffName]
  );

  const processUploadQueue = useCallback(async () => {
    if (uploadRunningRef.current) return;
    uploadRunningRef.current = true;
    try {
      while (uploadQueueRef.current.length) {
        const task = uploadQueueRef.current.shift();
        if (!task) continue;

        if (task.kind === "section") {
          const { secId, idx, photoId } = task;
          const currentValues = formDataRef.current?.[secId] || {};
          const items = normalizePhotoValue(currentValues[idx]);
          const target = items.find((it) => it?.id === photoId && isLocalPhotoItem(it));
          if (!target) continue;

          updatePhotoItem(secId, idx, photoId, { status: "uploading", progress: 0 });
          try {
            const meta = await uploadPhoto(orgId, storeId, secId, target.file, (pct) => {
              updatePhotoItem(secId, idx, photoId, { progress: pct, status: pct >= 100 ? "done" : "uploading" });
            });
            setFormData((d) => {
              const secValues = { ...(d[secId] || {}) };
              const nextItems = normalizePhotoValue(secValues[idx]).map((it) =>
                it?.id === photoId ? { ...toRemoteItem(meta), id: photoId } : it
              );
              secValues[idx] = nextItems;
              const nextData = { ...d, [secId]: secValues };
              formDataRef.current = nextData;
              void patchDraftAfterPhotoUpload(secId, secValues);
              return nextData;
            });
          } catch (err) {
            updatePhotoItem(secId, idx, photoId, { status: "error" });
            showToast("A photo failed to upload in the background.", "error");
          } finally {
            queuedPhotoIdsRef.current.delete(`section__${secId}__${idx}__${photoId}`);
          }
        }

        if (task.kind === "custom") {
          const { itemId, photoId } = task;
          const current = customReportRef.current || { title: "", items: [] };
          const item = (current.items || []).find((it) => it.id === itemId);
          const target = normalizePhotoValue(item?.value).find((it) => it?.id === photoId && isLocalPhotoItem(it));
          if (!target) continue;

          setCustomReport((prev) => ({
            ...prev,
            items: (prev.items || []).map((it) =>
              it.id !== itemId
                ? it
                : {
                    ...it,
                    value: normalizePhotoValue(it.value).map((p) =>
                      p?.id === photoId ? { ...p, status: "uploading", progress: 0 } : p
                    ),
                  }
            ),
          }));

          try {
            const meta = await uploadPhoto(orgId, storeId, CUSTOM_REPORT_ID, target.file, null);
            setCustomReport((prev) => {
              const next = {
                ...prev,
                items: (prev.items || []).map((it) =>
                  it.id !== itemId
                    ? it
                    : {
                        ...it,
                        value: normalizePhotoValue(it.value).map((p) =>
                          p?.id === photoId ? { ...toRemoteItem(meta), id: photoId } : p
                        ),
                      }
                ),
              };
              customReportRef.current = next;
              void patchCustomDraft(next);
              return next;
            });
          } catch (err) {
            showToast("A custom report photo failed to upload.", "error");
          } finally {
            queuedPhotoIdsRef.current.delete(`custom__${itemId}__${photoId}`);
          }
        }
      }
    } finally {
      uploadRunningRef.current = false;
    }
  }, [orgId, storeId, patchDraftAfterPhotoUpload, patchCustomDraft, showToast, updatePhotoItem]);

  const enqueueRegularPhoto = useCallback((secId, idx, photoId) => {
    const key = `section__${secId}__${idx}__${photoId}`;
    if (queuedPhotoIdsRef.current.has(key)) return;
    queuedPhotoIdsRef.current.add(key);
    uploadQueueRef.current.push({ kind: "section", secId, idx, photoId });
    void processUploadQueue();
  }, [processUploadQueue]);

  const enqueueCustomPhoto = useCallback((itemId, photoId) => {
    const key = `custom__${itemId}__${photoId}`;
    if (queuedPhotoIdsRef.current.has(key)) return;
    queuedPhotoIdsRef.current.add(key);
    uploadQueueRef.current.push({ kind: "custom", itemId, photoId });
    void processUploadQueue();
  }, [processUploadQueue]);

  const addCustomItem = useCallback((type) => {
    const id = makeId();
    setCustomReport((prev) => ({
      ...prev,
      items: [
        ...(prev.items || []),
        type === "subheading"
          ? { id, type, label: "", value: "" }
          : type === "check"
          ? { id, type, label: "", value: null }
          : type === "photo"
          ? { id, type, label: "", value: [] }
          : { id, type: "text", label: "", value: "" },
      ],
    }));
  }, []);

  const updateCustomItem = useCallback((itemId, patch) => {
    const current = customReportRef.current || { title: "", items: [] };
    const next = {
      ...current,
      items: (current.items || []).map((it) => (it.id === itemId ? { ...it, ...patch } : it)),
    };
    customReportRef.current = next;
    setCustomReport(next);
  }, []);

  const removeCustomItem = useCallback((itemId) => {
    setCustomReport((prev) => {
      const target = (prev.items || []).find((it) => it.id === itemId);
      if (target?.type === "photo") {
        normalizePhotoValue(target.value).forEach((p) => {
          if (p?.previewUrl) revokePreviewUrl(p.previewUrl);
        });
      }
      const next = {
        ...prev,
        items: (prev.items || []).filter((it) => it.id !== itemId),
      };
      customReportRef.current = next;
      return next;
    });
  }, [revokePreviewUrl]);

  const hasPendingCustomUploads = useMemo(
    () =>
      (customReport.items || []).some(
        (item) =>
          item.type === "photo" &&
          normalizePhotoValue(item.value).some((p) => isLocalPhotoItem(p) || p?.status === "uploading")
      ),
    [customReport]
  );

  useEffect(() => {
    saveSubheadingRef.current = saveSubheading;
  }, [saveSubheading]);

  const submitCustomReport = useCallback(async () => {
    if (customSubmitting) return;

    const title = String(customReportRef.current?.title || "").trim();
    const items = customReportRef.current?.items || [];
    if (!title) {
      showToast("Name the custom report first.", "error");
      return;
    }
    if (!items.length) {
      showToast("Add at least one custom field or subheading.", "error");
      return;
    }

    setCustomSubmitting(true);
    try {
      const normalizedItems = [];
      for (const item of items) {
        if (item.type === "photo") {
          const metas = await uploadValueToMetas(orgId, storeId, CUSTOM_REPORT_ID, normalizePhotoValue(item.value));
          normalizedItems.push({
            ...item,
            value: Array.isArray(metas) ? metas : metas ? [metas] : [],
          });
        } else {
          normalizedItems.push(item);
        }
      }

      await setDoc(
        doc(
          db,
          "orgs",
          orgId,
          "stores",
          storeId,
          COLL.submissions,
          `${CUSTOM_REPORT_ID}_${Date.now()}`
        ),
        {
          orgId,
          storeId,
          sectionName: "Custom Report",
          sectionId: CUSTOM_REPORT_ID,
          submittedBy: staffName || "Unknown",
          submittedAt: serverTimestamp(),
          customReport: {
            title,
            items: normalizedItems.map((item) => ({
              ...item,
              value: item.type === "photo" ? item.value : item.value,
            })),
          },
        }
      );

      await setDoc(
        doc(db, "orgs", orgId, "stores", storeId, COLL.drafts, CUSTOM_REPORT_ID),
        {
          orgId,
          storeId,
          sectionId: CUSTOM_REPORT_ID,
          sectionName: "Custom Report",
          customReport: { title: "", items: [] },
          updatedBy: staffName || "Unknown",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setCustomReport({ title: "", items: [] });
      customReportRef.current = { title: "", items: [] };
      showToast("Custom report submitted.", "success");
    } catch (err) {
      console.error(err);
      showToast("Custom report failed to submit.", "error");
    } finally {
      setCustomSubmitting(false);
    }
  }, [customSubmitting, orgId, storeId, staffName, showToast]);


return (
  <div className="page" style={styles.page}>
    <style>{`@keyframes neoSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <div style={styles.surface}>
<div style={styles.hero}>
  <div style={styles.heroGlow} />
  <div style={styles.heroBadge}>Neo Workspace</div>

  <h1 style={styles.title}>
    <span style={styles.titleMain}>Staff Workspace</span>
    <span style={styles.titleStore}>{storeName}</span>
  </h1>

  <div style={styles.welcome}>Hi {staffName}</div>
</div>

      {/* Global autosave status */}
      <div
        style={{
          ...styles.pill,
          ...(pill.kind === "ok"
            ? styles.pillOk
            : pill.kind === "warn"
            ? styles.pillWarn
            : pill.kind === "error"
            ? styles.pillErr
            : styles.pillSaving),
        }}
      >
        {pill.text}
      </div>

      <ConfirmModal
        styles={styles}
        isOpen={!!confirmSec}
        title="Confirm Submit"
        message={`Submit “${
          sections.find((s) => s.id === confirmSec)?.name || ""
        }”?`}
        confirmText={submitting ? "Submitting…" : "Yes"}
        cancelText="Cancel"
        onConfirm={() => {
          if (submitting) return;
          const sec = sections.find((s) => s.id === confirmSec);
          if (sec) doSubmit(sec);
        }}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmModal
        styles={styles}
        isOpen={confirmCustomSubmit}
        title="Submit Custom Report"
        message={`Submit this custom report as ${staffName || "Unknown"}?`}
        confirmText={customSubmitting ? "Submitting…" : "Submit"}
        cancelText="Cancel"
        onConfirm={() => {
          if (customSubmitting) return;
          setConfirmCustomSubmit(false);
          submitCustomReport();
        }}
        onCancel={() => setConfirmCustomSubmit(false)}
      />

      <WorkspaceTabs activeTab={workspaceTab} onChange={setWorkspaceTab} themeMode={themeMode} />

      {workspaceTab === "intelligence" && (
        <ExecutionIntelligencePanel
          rows={displayNeoDutyRows}
          loading={neoDutyLoading}
          readError={neoDutyReadError}
          onComplete={null}
          onOpenDuty={openStandardDutyFromIntelligence}
          onSnoozeDuty={snoozeNeoDuty}
          onAcknowledgeDuty={acknowledgeNeoDutyReminder}
          completing={completingNeoDutyId}
          themeMode={themeMode}
        />
      )}

      {workspaceTab === "standards" && (
        <>
      <div id="neo-standards-workspace" style={styles.container}>
        {neoDutyRows.filter(item => item.completionSource === 'morpheus' && !(sections || []).some(sec=>sec.id===(item.staffDashboardSectionId || item.standardId))).map(item => (
          <div key={item.id} style={styles.card}>
            <h3 style={{margin:0}}>{item.dutyLabel || item.standardName}</h3>
            <div style={{marginTop:8, fontSize:13}}>Morpheus count · due {item.dueTime} · {isDutyCompleted(item) ? 'Complete' : 'Waiting for required count'}</div>
            <MorpheusCountProgress item={item} />
            {item.completedAt && <div style={{marginTop:8,fontSize:12}}>Completed by {item.completedBy} · {item.completionStatus === 'late' ? 'Late' : 'On time'}</div>}
          </div>
        ))}
        {sections.length === 0 && (
          <div style={styles.empty}>No sections found for this store.</div>
        )}

        {sections.map((sec) => {
          const stamp = lastSubmitted[sec.id];
          const groups = groupFields(sec);
          const saves = subSaves?.[sec.id] || {};
          const allSaved = groups.every((g) => (g.items || []).some(it=>it.f.completionSource!=='morpheus') ? !!saves?.[g.key]?.saved : (g.items || []).every(it=>fieldDone(it.f,null,sec.id)));

          // Section progress: counts all fields (checkbox/photo/text)
          const secFields = sec.fields || [];
          const total = secFields.length;
          const done = secFields.reduce((acc, f, idx) => {
            const v = formData?.[sec.id]?.[idx];
            return acc + (fieldDone(f, v, sec.id) ? 1 : 0);
          }, 0);
          const pct = total ? Math.round((done / total) * 100) : 0;

          return (
            <div
              key={sec.id}
              id={`neo-section-${sec.id}`}
              style={{
                ...styles.card,
                ...(expandedId === sec.id ? styles.cardActive : null),
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  expandedId === sec.id ? styles.cardActive.boxShadow : styles.cardHover.boxShadow;
                e.currentTarget.style.borderColor =
                  expandedId === sec.id ? styles.cardActive.borderColor : styles.cardHover.borderColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  expandedId === sec.id ? styles.cardActive.boxShadow : styles.card.boxShadow;
                e.currentTarget.style.borderColor =
                  expandedId === sec.id ? styles.cardActive.borderColor : styles.card.borderColor;
              }}
            >
              <div
                style={{
                  ...styles.header,
                  ...(expandedId === sec.id ? styles.headerActive : null),
                }}
                onClick={() => handleToggle(sec.id)}
              >
                  <div style={{ minWidth: 0 }}>
                  <h2 style={styles.secTitle}>
                    {sec.name}
                    {sec.isFixedCashup ? " (fixed)" : ""}
                  </h2>
                  {String(sec?.description || "").trim() ? (
                    <div
                      style={{
                        marginTop: 6,
                        color: themeMode === "light" ? "#172033" : "rgba(191,219,254,.78)",
                        fontSize: 12,
                        lineHeight: 1.45,
                        maxWidth: 760,
                      }}
                    >
                      {String(sec.description).trim()}
                    </div>
                  ) : null}
                </div>

                <div style={styles.headerRight}>
                  <span style={styles.progressText}>
                    {done}/{total} done • {pct}%
                  </span>
                  <div style={styles.progressOuterSm}>
                    <div style={{ ...styles.progressInnerSm, width: `${pct}%` }} />
                  </div>
                  <span aria-hidden>{expandedId === sec.id ? "▾" : "▸"}</span>
                </div>
              </div>

              <div style={styles.stampRow}>
                {stamp?.by ? (
                  <span style={styles.stamp}>
                    Last submitted by <strong>{stamp.by}</strong>
                    {stamp?.at ? ` at ${fmtDateTime(stamp.at)}` : ""}
                    {stamp?.elapsedCompletionSeconds ? ` • Completed in ${formatDurationSeconds(stamp.elapsedCompletionSeconds)}` : ""}
                  </span>
                ) : (
                  <span style={styles.stampEmpty}>No submissions yet.</span>
                )}
              </div>
              
              {expandedId === sec.id && (
                <div style={styles.body}>
                  {groups.map((g, gi) => {
                    const showHeader = !!g.subheading;
                    const gSave = saves?.[g.key] || null;
                    const ui = subUi?.[`${sec.id}__${g.key}`] || {};

                    const gTotal = (g.items || []).length;
                    const gDone = (g.items || []).reduce((acc, it) => {
                      const v = formData?.[sec.id]?.[it.idx];
                      return acc + (fieldDone(it.f, v, sec.id) ? 1 : 0);
                    }, 0);
                    const gPct = gTotal ? Math.round((gDone / gTotal) * 100) : 0;
                    const sourceOnly = gTotal > 0 && g.items.every(({ f }) => f.completionSource === "morpheus");
                    const sourceFiled = sourceOnly && g.items.every(({ f }) =>
                      isDutyFiledWithSection(findMorpheusOccurrence(sec.id, f, neoDutyRows), lastSubmitted[sec.id]));
                    return (
                      <div
                        key={gi}
                        id={`neo-subheading-${sec.id}-${normalizedSubheadingTargetKey(g.subheading).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "general"}`}
                        style={{
                          ...styles.groupBox,
                          ...(gi === 0 && !showHeader
                            ? { borderTopWidth: 0 }
                            : {}),
                          ...(standardFocus?.secId === sec.id && standardFocus?.targetType === "subheading" && normalizedSubheadingCompareKey(standardFocus?.targetKey || "") === normalizedSubheadingCompareKey(g.subheading || "")
                            ? { borderColor: "rgba(45,212,191,.95)", boxShadow: "0 0 0 2px rgba(45,212,191,.18), 0 18px 40px rgba(45,212,191,.12)" }
                            : {}),
                        }}
                      >
                        <div style={styles.groupHeader}>
                          <div style={styles.groupHeaderLeft}>
                            {showHeader ? (
                              <span style={styles.groupTitle}>{g.subheading}</span>
                            ) : (
                              <span style={styles.groupTitleMuted}>Subheading</span>
                            )}

                            <span style={styles.groupProgress}>
                              {gDone}/{gTotal} • {gPct}%
                            </span>

                            {sourceOnly ? (
                              <span style={styles.groupSavedMeta}>{sourceFiled ? "Submitted to Reports" : gDone === gTotal ? "Morpheus count ready" : "Waiting for Morpheus count"}</span>
                            ) : ui?.state === "saving" ? (
                              <span style={styles.groupUnsavedMeta}>{ui.msg || "Saving..."}</span>
                            ) : ui?.state === "uploading" ? (
                              <span style={styles.groupUnsavedMeta}>{ui.msg || "Uploading photos…"}</span>
                            ) : ui?.state === "error" ? (
                              <span style={{ ...styles.groupUnsavedMeta, color: "#b02a37" }}>
                                {ui.msg || "Upload failed"}
                              </span>
                            ) : ui?.state === "dirty" && Date.now() - Number(ui?.updatedAt || 0) < 2500 ? (
                              <span style={styles.groupUnsavedMeta}>{ui.msg || "Unsaved changes…"}</span>
                            ) : gSave?.saved ? (
                              <span style={styles.groupSavedMeta}>
                                Saved by <strong>{gSave.savedBy || "Unknown"}</strong>
                                {gSave.savedAt ? ` at ${fmtDateTime(gSave.savedAt)}` : ""}
                              </span>
                            ) : (
                              <span style={styles.groupUnsavedMeta}>Not saved yet</span>
                            )}
                          </div>
                        </div>

                        <div style={styles.groupProgressBarOuter}>
                          <div style={{ ...styles.groupProgressBarInner, width: `${gPct}%` }} />
                        </div>

                        <div style={styles.rows}>
                          {g.items.map(({ idx, f }) => {
                            const v = formData[sec.id]?.[idx];
                            const photoItems = f.type === "photo" ? (Array.isArray(v) ? v : normalizePhotoValue(v)) : [];
                            const dutyState = dutyCompletionStateForField(sec, idx, f);
                            const dutyLocked = dutyState.completed;
                            const dutyRowStyle = {
                              ...styles.row,
                              ...(dutyLocked
                                ? {
                                    borderColor: "rgba(52,211,153,.48)",
                                    background: "rgba(16,185,129,.055)",
                                    boxShadow: "0 0 0 1px rgba(16,185,129,.08)",
                                  }
                                : {}),
                              ...(standardFocus?.secId === sec.id && standardFocus?.idx === idx
                                ? { borderColor: "rgba(45,212,191,.95)", boxShadow: "0 0 0 2px rgba(45,212,191,.22), 0 18px 40px rgba(45,212,191,.16)" }
                                : {}),
                            };

                            return (
                              <div
                                key={idx}
                                id={`neo-duty-field-${sec.id}-${idx}`}
                                style={dutyRowStyle}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = styles.rowHover.background;
                                  e.currentTarget.style.borderColor = styles.rowHover.borderColor;
                                  e.currentTarget.style.boxShadow = styles.rowHover.boxShadow;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = dutyRowStyle.background;
                                  e.currentTarget.style.borderColor = dutyRowStyle.borderColor;
                                  e.currentTarget.style.boxShadow = dutyRowStyle.boxShadow;
                                }}
                              >
                                <div style={styles.cellLabel}>
                                  <div style={styles.fieldLabel}>{f.label}</div>
                                  {String(f?.instructions || "").trim() ? (
                                    <div
                                      style={{
                                        marginTop: 7,
                                        maxWidth: 520,
                                        color: "rgba(191,219,254,.70)",
                                        fontSize: 12,
                                        lineHeight: 1.45,
                                        fontWeight: 600,
                                      }}
                                    >
                                      {String(f.instructions).trim()}
                                    </div>
                                  ) : null}
                                </div>

                                <div style={styles.cellInput}>
                                  {f.completionSource==='morpheus' ? (
                                    <div style={{width:'100%',textAlign:'left',padding:12,border:'1px solid rgba(34,211,238,.25)',borderRadius:10}}>
                                      <strong>{dutyState.filed?'Count submitted':dutyState.completed?'✓ Count complete in Morpheus':dutyState.queueItem?'Waiting for Morpheus count':fieldDone(f,null,sec.id)?'No count scheduled today':'Waiting for the count duty to load'}</strong>
                                      {dutyState.filed ? <div style={{marginTop:8,fontSize:12}}>Recorded in Reports. The next count will appear when scheduled.</div> : dutyState.queueItem && <MorpheusCountProgress item={dutyState.queueItem}/>}
                                      {dutyState.completed && <div style={{marginTop:8,fontSize:12}}>Completed by {dutyState.queueItem.completedBy || 'Morpheus'} · {dutyState.queueItem.completionStatus==='late'?'Late':'On time'}</div>}
                                    </div>
                                  ) : f.type === "checkbox" ? (
                                    <div style={styles.binaryWrap}>
                                      <button
                                        type="button"
                                        disabled={dutyLocked}
                                        onClick={() => !dutyLocked && handleChange(sec.id, idx, f.type, true)}
                                        style={{
                                          ...styles.binaryBtn,
                                          ...(formData[sec.id]?.[idx] === true ? styles.binaryBtnYesActive : {}),
                                          ...(dutyLocked ? { opacity: 0.7, cursor: "not-allowed" } : {}),
                                        }}
                                      >
                                        ✓ Yes
                                      </button>
                                      <button
                                        type="button"
                                        disabled={dutyLocked}
                                        onClick={() => !dutyLocked && handleChange(sec.id, idx, f.type, false)}
                                        style={{
                                          ...styles.binaryBtn,
                                          ...(formData[sec.id]?.[idx] === false ? styles.binaryBtnNoActive : {}),
                                          ...(dutyLocked ? { opacity: 0.7, cursor: "not-allowed" } : {}),
                                        }}
                                      >
                                        ✕ No
                                      </button>
                                    </div>
                                  ) : f.type === "photo" ? (
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, width: "100%" }}>
                                      {(() => {
                                        const photoState = getPhotoUploadState(v);
                                        return (
                                          <>
                                      {!dutyLocked ? (
                                      <div style={styles.photoHeaderRow}>
                                        <div style={styles.photoStatusSlot}>
                                          {photoState.totalActive > 0 ? (
                                            <div style={styles.photoUploadStatus}>
                                              <span style={styles.photoUploadSpinner} />
                                              <span>
                                                {photoState.uploading > 0
                                                  ? `Uploading ${photoState.uploading} photo${photoState.uploading === 1 ? "" : "s"}...`
                                                  : `Queued ${photoState.pending} photo${photoState.pending === 1 ? "" : "s"}...`}
                                              </span>
                                            </div>
                                          ) : null}
                                        </div>
                                        {/* Two pickers: Camera + Gallery (mobile Safari respects capture) */}
<div style={styles.photoActionSlot}>
  {/* CAMERA */}
  <label
    style={styles.pickBtn}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = styles.pickBtnHover.boxShadow;
      e.currentTarget.style.borderColor = styles.pickBtnHover.borderColor;
      e.currentTarget.style.transform = styles.pickBtnHover.transform;
      e.currentTarget.style.background = styles.pickBtnHover.background;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = styles.pickBtn.boxShadow;
      e.currentTarget.style.borderColor = styles.pickBtn.borderColor;
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.background = styles.pickBtn.background;
    }}
  >
    📷 Take photo
    <input
      type="file"
      accept="image/*"
      capture="environment"
      multiple
      onChange={(e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const tooBig = files.find((x) => x.size > 5 * 1024 * 1024);
        if (tooBig) {
          showToast(
            "Photo too large (max 5MB each (Storage rules)). Please take a smaller photo.",
            "error"
          );
          e.target.value = "";
          return;
        }

        const incoming = files.map((file) => makeLocalItem(file));
        trackPreviewUrls(incoming);

        const existing = Array.isArray(formData[sec.id]?.[idx])
          ? formData[sec.id]?.[idx]
          : normalizePhotoValue(formData[sec.id]?.[idx]);

        handleChange(sec.id, idx, f.type, [...existing, ...incoming]);
        incoming.forEach((item) => enqueueRegularPhoto(sec.id, idx, item.id));
        e.target.value = "";
      }}
      style={styles.hiddenFile}
    />
  </label>

  {/* GALLERY / FILES */}
  {staffUploadsEnabled && (
    <label
      style={styles.pickBtnAlt}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = styles.pickBtnAltHover.boxShadow;
        e.currentTarget.style.borderColor = styles.pickBtnAltHover.borderColor;
        e.currentTarget.style.transform = styles.pickBtnAltHover.transform;
        e.currentTarget.style.background = styles.pickBtnAltHover.background;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = styles.pickBtnAlt.boxShadow;
        e.currentTarget.style.borderColor = styles.pickBtnAlt.borderColor;
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.background = styles.pickBtnAlt.background;
      }}
    >
      ⬆️ Upload
      <input
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (!files.length) return;

          const tooBig = files.find((x) => x.size > 5 * 1024 * 1024);
          if (tooBig) {
            showToast(
              "Photo too large (max 5MB each (Storage rules)). Please take a smaller photo.",
              "error"
            );
            e.target.value = "";
            return;
          }

          const incoming = files.map((file) => makeLocalItem(file));
          trackPreviewUrls(incoming);

          const existing = Array.isArray(formData[sec.id]?.[idx])
            ? formData[sec.id]?.[idx]
            : normalizePhotoValue(formData[sec.id]?.[idx]);

          handleChange(sec.id, idx, f.type, [...existing, ...incoming]);
          e.target.value = "";
        }}
        style={styles.hiddenFile}
      />
    </label>
  )}
</div>
                                        <div style={styles.photoClearSlot}>
                                          {photoItems.length ? (
                                            <button
                                              type="button"
                                              onClick={() => clearPhotos(sec.id, idx)}
                                              style={styles.clearBtn}
                                            >
                                              Clear all
                                            </button>
                                          ) : null}
                                        </div>
                                      </div>
                                      ) : (
                                        <div style={{
                                          alignSelf: "stretch",
                                          textAlign: "right",
                                          fontSize: 12,
                                          color: "#6ee7b7",
                                          fontWeight: 700,
                                        }}>
                                          Response locked after duty completion
                                        </div>
                                      )}

                                      {/* Thumbnails */}
                                      {photoItems.length ? (
                                        <div style={styles.thumbWrap}>
                                          {photoItems.map((it) => {
                                            const src = isLocalPhotoItem(it)
                                              ? it.previewUrl
                                              : isRemotePhotoItem(it)
                                              ? it.url
                                              : isMetaObj(it)
                                              ? it.url
                                              : "";

                                            const prog = Number(it?.progress || 0);
                                            const isUploading = it?.status === "uploading" && prog < 100;

                                            return (
                                              <div key={it.id || it.url || makeId()} style={styles.thumb}>
                                                {src ? (
                                                  <img
                                                    src={src}
                                                    alt={it.name || "photo"}
                                                    style={styles.thumbImg}
                                                  />
                                                ) : (
                                                  <div style={styles.thumbPlaceholder}>Photo</div>
                                                )}

                                                {/* progress bar */}
                                                {isUploading ? (
                                                  <>
                                                    <div style={styles.thumbUploadingOverlay}>
                                                      <span style={styles.photoUploadSpinner} />
                                                      <span style={styles.thumbUploadingText}>{prog > 0 ? `${prog}%` : "Uploading..."}</span>
                                                    </div>
                                                    <div style={styles.progressOuter}>
                                                      <div style={{ ...styles.progressInner, width: `${Math.max(2, Math.min(100, prog))}%` }} />
                                                    </div>
                                                  </>
                                                ) : null}

                                                <div style={styles.thumbFooter}>
                                                  <span style={styles.thumbName} title={it.name || ""}>
                                                    {String(it.name || "photo").slice(0, 18)}
                                                    {String(it.name || "").length > 18 ? "…" : ""}
                                                  </span>

                                                  <button
                                                    type="button"
                                                    disabled={dutyLocked}
                                                    onClick={() => !dutyLocked && removePhotoItem(sec.id, idx, it.id)}
                                                    style={{
                                                      ...styles.thumbDel,
                                                      ...(dutyLocked ? { opacity: 0.35, cursor: "not-allowed" } : {}),
                                                    }}
                                                    title="Remove"
                                                  >
                                                    ✕
                                                  </button>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      ) : (
                                        <div style={styles.photoHint}>
                                          Tip: you can take multiple photos before saving.
                                        </div>
                                      )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                  ) : (
                                    <input
                                      type={f.type === "number" ? "number" : "text"}
                                      value={formData[sec.id]?.[idx] ?? ""}
                                      disabled={dutyLocked}
                                      onChange={(e) =>
                                        !dutyLocked && handleChange(sec.id, idx, f.type, e.target.value)
                                      }
                                      style={{
                                        ...styles.input,
                                        ...(dutyLocked ? { opacity: 0.72, cursor: "not-allowed" } : {}),
                                      }}
                                    />
                                  )}
                                </div>

                                {dutyState.queueItem && f.completionSource!=='morpheus' ? (
                                  <div style={{
                                    gridColumn: "1 / -1",
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                    gap: 10,
                                    paddingTop: 10,
                                    marginTop: 6,
                                    borderTop: "1px solid rgba(148,163,184,.12)",
                                  }}>
                                    {dutyLocked ? (
                                      <>
                                        <span style={{
                                          display: "inline-flex",
                                          padding: "5px 9px",
                                          borderRadius: 999,
                                          border: "1px solid rgba(52,211,153,.34)",
                                          background: "rgba(16,185,129,.12)",
                                          color: "#6ee7b7",
                                          fontSize: 11,
                                          fontWeight: 800,
                                          letterSpacing: ".04em",
                                          textTransform: "uppercase",
                                        }}>
                                          ✓ Duty complete
                                        </span>
                                        <span style={{ fontSize: 12, color: "#6ee7b7", fontWeight: 700 }}>
                                          Completed by {dutyState.queueItem.completedBy || "staff"}
                                        </span>
                                        <button
                                          type="button"
                                          disabled={completingNeoDutyId === dutyState.queueItem.id}
                                          onClick={() => reopenFieldDuty(sec, idx, f)}
                                          style={{
                                            ...styles.smallGhostBtn,
                                            borderColor: "rgba(96,165,250,.35)",
                                            color: "#93c5fd",
                                          }}
                                        >
                                          Reopen duty
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        type="button"
                                        disabled={!dutyState.canComplete || completingNeoDutyId === dutyState.queueItem.id}
                                        onClick={() => markFieldDutyComplete(sec, idx, f)}
                                        style={{
                                          ...styles.smallGhostBtn,
                                          borderColor: dutyState.canComplete ? "rgba(52,211,153,.42)" : "rgba(148,163,184,.18)",
                                          background: dutyState.canComplete ? "rgba(16,185,129,.10)" : "rgba(148,163,184,.05)",
                                          color: dutyState.canComplete ? "#6ee7b7" : "rgba(203,213,225,.5)",
                                          cursor: dutyState.canComplete ? "pointer" : "not-allowed",
                                        }}
                                        title={
                                          !dutyState.hasResponse
                                            ? "Complete the response first"
                                            : dutyState.uploadsPending
                                            ? "Wait for uploads to finish"
                                            : "Mark this duty complete"
                                        }
                                      >
                                        {completingNeoDutyId === dutyState.queueItem.id
                                          ? "Completing…"
                                          : dutyState.uploadsPending
                                          ? "Waiting for upload…"
                                          : "✓ Mark duty complete"}
                                      </button>
                                    )}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
</div>
                    );
                  })}

                  {(() => {
                    const isComplete = total > 0 && done === total;
                    const canSubmit = allSaved && isComplete;
                    const hasFiledCount = secFields.some((field) => field.completionSource === "morpheus" &&
                      isDutyFiledWithSection(findMorpheusOccurrence(sec.id, field, neoDutyRows), lastSubmitted[sec.id]));
                    const disabledReason = hasFiledCount
                      ? "Morpheus count submitted. The next count will appear when scheduled."
                      : !isComplete
                      ? "Complete this section to 100% to enable Submit."
                      : !allSaved
                      ? "Waiting for autosave to finish…"
                      : "";

                    return (
                      <div style={styles.actionsRow}>
                        <button
                          disabled={submitting || !canSubmit}
                          onClick={() => trySubmit(sec)}
                          style={{
                            ...styles.actionBtn,
                            ...styles.submitBtn,
                            ...(!canSubmit
                              ? { opacity: 0.5, cursor: "not-allowed" }
                              : {}),
                            ...(submitting
                              ? { opacity: 0.6, cursor: "not-allowed" }
                              : {}),
                          }}
                        >
                          {submitting
                            ? "Submitting…"
                            : canSubmit
                            ? "Submit"
                            : hasFiledCount
                            ? "Awaiting next count"
                            : !isComplete
                            ? "Complete section to submit"
                            : "Saving…"}
                        </button>
                        {!canSubmit && (
                          <div style={styles.submitNote}>{disabledReason}</div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div
        style={{
          ...styles.card,
          ...styles.customCard,
        }}
      >
        <div style={styles.header} onClick={() => setExpanded((curr) => (curr === CUSTOM_REPORT_ID ? null : CUSTOM_REPORT_ID))}>
          <h2 style={styles.secTitle}>Custom Report</h2>
          <div style={styles.headerRight}>
            <span style={styles.progressText}>Fixed section</span>
            <span aria-hidden>{expandedId === CUSTOM_REPORT_ID ? "▾" : "▸"}</span>
          </div>
        </div>

        <div style={styles.stampRow}>
          <span style={styles.stamp}>
            Build and submit an ad-hoc report with your own subheadings, checks, text fields, and photos.
          </span>
        </div>

        {expandedId === CUSTOM_REPORT_ID && (
          <div style={styles.body}>
            <div style={styles.customPanel}>
              <div style={styles.customToolbar}>
                <input
                  type="text"
                  value={customReport.title}
                  onChange={(e) => setCustomReport((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Custom report name"
                  style={{ ...styles.input, maxWidth: "100%" }}
                />
                <div style={styles.customToolbarBtns}>
                  <button type="button" style={styles.smallGhostBtn} onClick={() => addCustomItem("subheading")}>+ Subheading</button>
                  <button type="button" style={styles.smallGhostBtn} onClick={() => addCustomItem("text")}>+ Text field</button>
                  <button type="button" style={styles.smallGhostBtn} onClick={() => addCustomItem("check")}>+ Check field</button>
                  <button type="button" style={styles.smallGhostBtn} onClick={() => addCustomItem("photo")}>+ Photo field</button>
                </div>
              </div>

              {(customReport.items || []).length === 0 ? (
                <div style={styles.emptyInline}>No custom fields added yet.</div>
              ) : (
                <div style={styles.customItems}>
                  {customReport.items.map((item, idx) => (
                    <div key={item.id} style={styles.customItemCard}>
                      <div style={styles.customItemTop}>
                        <div style={styles.customItemMeta}>
                          <span style={styles.customItemIndex}>{idx + 1}</span>
                          <span style={styles.customItemType}>{item.type === "check" ? "Check" : item.type === "photo" ? "Photo" : item.type === "subheading" ? "Subheading" : "Text"}</span>
                        </div>
                        <button type="button" style={styles.removeMiniBtn} onClick={() => removeCustomItem(item.id)}>Remove</button>
                      </div>

                      {item.type === "subheading" ? (
                        <input
                          type="text"
                          value={item.value || ""}
                          onChange={(e) => updateCustomItem(item.id, { value: e.target.value })}
                          placeholder="Subheading title"
                          style={{ ...styles.input, maxWidth: "100%" }}
                        />
                      ) : (
                        <>
                          <input
                            type="text"
                            value={item.label || ""}
                            onChange={(e) => updateCustomItem(item.id, { label: e.target.value })}
                            placeholder="Field label"
                            style={{ ...styles.input, maxWidth: "100%", marginBottom: 10 }}
                          />

                          {item.type === "text" ? (
                            <input
                              type="text"
                              value={item.value || ""}
                              onChange={(e) => updateCustomItem(item.id, { value: e.target.value })}
                              placeholder="Enter response"
                              style={{ ...styles.input, maxWidth: "100%" }}
                            />
                          ) : item.type === "check" ? (
                            <div style={styles.binaryWrap}>
                              <button
                                type="button"
                                onClick={() => updateCustomItem(item.id, { value: true })}
                                style={{
                                  ...styles.binaryBtn,
                                  ...(item.value === true ? styles.binaryBtnYesActive : {}),
                                }}
                              >
                                ✓ Yes
                              </button>
                              <button
                                type="button"
                                onClick={() => updateCustomItem(item.id, { value: false })}
                                style={{
                                  ...styles.binaryBtn,
                                  ...(item.value === false ? styles.binaryBtnNoActive : {}),
                                }}
                              >
                                ✕ No
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <label style={styles.pickBtn}>
                                  📷 Take photo
                                  <input
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    multiple
                                    onChange={(e) => {
                                      const files = Array.from(e.target.files || []);
                                      if (!files.length) return;
                                      const incoming = files.map((file) => makeLocalItem(file));
                                      trackPreviewUrls(incoming);
                                      updateCustomItem(item.id, {
                                        value: [...normalizePhotoValue(item.value), ...incoming],
                                      });
                                      incoming.forEach((photo) => enqueueCustomPhoto(item.id, photo.id));
                                      e.target.value = "";
                                    }}
                                    style={styles.hiddenFile}
                                  />
                                </label>

                                {staffUploadsEnabled && (
                                  <label style={styles.pickBtnAlt}>
                                    ⬆️ Upload
                                    <input
                                      type="file"
                                      accept="image/*"
                                      multiple
                                      onChange={(e) => {
                                        const files = Array.from(e.target.files || []);
                                        if (!files.length) return;
                                        const incoming = files.map((file) => makeLocalItem(file));
                                        trackPreviewUrls(incoming);
                                        updateCustomItem(item.id, {
                                          value: [...normalizePhotoValue(item.value), ...incoming],
                                        });
                                        incoming.forEach((photo) => enqueueCustomPhoto(item.id, photo.id));
                                        e.target.value = "";
                                      }}
                                      style={styles.hiddenFile}
                                    />
                                  </label>
                                )}
                              </div>

                              {normalizePhotoValue(item.value).length ? (
                                <div style={styles.thumbWrap}>
                                  {normalizePhotoValue(item.value).map((photo) => {
                                    const src = isLocalPhotoItem(photo) ? photo.previewUrl : photo.url;
                                    return (
                                      <div key={photo.id} style={styles.thumb}>
                                        <img src={src} alt={photo.name || "photo"} style={styles.thumbImg} />
                                        <div style={styles.thumbFooter}>
                                          <span style={styles.thumbName}>{String(photo.name || "photo").slice(0, 18)}</span>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              updateCustomItem(item.id, {
                                                value: normalizePhotoValue(item.value).filter((p) => p.id !== photo.id),
                                              })
                                            }
                                            style={styles.thumbDel}
                                          >
                                            ✕
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div style={styles.photoHint}>Add an optional photo for this custom field.</div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div style={styles.actionsRow}>
                <div style={styles.submitAsNote}>Submitting as {staffName || "Unknown"}</div>
                <button
                  type="button"
                  disabled={customSubmitting}
                  onClick={() => setConfirmCustomSubmit(true)}
                  style={{
                    ...styles.actionBtn,
                    ...styles.submitBtn,
                    ...(customSubmitting ? { opacity: 0.55, cursor: "not-allowed" } : {}),
                  }}
                >
                  {customSubmitting ? "Submitting…" : "Submit Custom Report"}
                </button>
                {(customSubmitting || hasPendingCustomUploads) && (
                  <div style={styles.submitNote}>
                    {customSubmitting ? "Submitting your custom report…" : "Photos are still uploading in the background, but submit will still continue."}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
        </>
      )}

            {/* Back button moved away from iPad top corners */}
      <button
        style={styles.backBtn}
        onClick={() => { try { clearStaffSession(); } catch {} navigate("/"); }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = styles.backBtnHover.boxShadow;
          e.currentTarget.style.transform = styles.backBtnHover.transform;
          e.currentTarget.style.filter = styles.backBtnHover.filter;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = styles.backBtn.boxShadow;
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.filter = "none";
        }}
      >
        ← Back
      </button>
    </div>

    <Toast
      styles={styles}
      message={toast.message}
      type={toast.type}
      onClose={() => setToast({ message: "", type: "info" })}
    />
  </div>
);
}

/* ---------------------------- styles --------------------------- */
const getStyles = (themeMode = "dark") => {
  const isLight = themeMode === "light";

  const c = {
    pageText: isLight ? "#14253a" : "#e6eef8",
    shellBg: isLight
      ? "radial-gradient(circle at 50% 0%, rgba(42,127,255,0.10), transparent 20%), radial-gradient(circle at 0% 50%, rgba(42,127,255,0.05), transparent 26%), linear-gradient(180deg, #f3f7fc, #eef3f9)"
      : "radial-gradient(circle at 50% 0%, rgba(77,162,255,0.18), transparent 20%), radial-gradient(circle at 0% 50%, rgba(0,130,255,0.08), transparent 26%), linear-gradient(180deg, rgba(3,10,22,0.99), rgba(2,8,18,1))",
    shellBorder: isLight ? "rgba(42,127,255,0.10)" : "rgba(98,165,255,0.14)",
    shellShadow: isLight
      ? "0 20px 55px rgba(23,43,77,0.08), inset 0 1px 0 rgba(255,255,255,0.9)"
      : "0 28px 90px rgba(0,0,0,0.52), inset 0 1px 0 rgba(255,255,255,0.04), inset 0 0 0 1px rgba(41,111,204,0.04)",

    heroBg: isLight
      ? "linear-gradient(180deg, #ffffff, #f5f9ff)"
      : "linear-gradient(180deg, rgba(9,24,46,0.98), rgba(6,17,34,0.95))",
    heroBorder: isLight ? "rgba(42,127,255,0.10)" : "rgba(97,171,255,0.16)",
    heroShadow: isLight
      ? "0 10px 30px rgba(28,52,84,0.08), inset 0 1px 0 rgba(255,255,255,0.95)"
      : "0 16px 34px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(0,132,255,0.04)",

    heroBadgeText: isLight ? "#2a7fff" : "#7db8ff",
    heroBadgeBg: isLight ? "rgba(42,127,255,0.08)" : "rgba(57,129,255,0.10)",
    heroBadgeBorder: isLight ? "rgba(42,127,255,0.14)" : "rgba(125,184,255,0.18)",

    titleMain: isLight ? "#10243b" : "#f7fbff",
    titleStore: isLight ? "#2a7fff" : "#53a7ff",
    welcome: isLight ? "#617790" : "#b7c7db",

    pillOkBg: isLight ? "rgba(32,183,109,0.10)" : "rgba(61,220,151,0.12)",
    pillOkText: isLight ? "#14995a" : "#6ef0b1",
    pillOkBorder: isLight ? "rgba(32,183,109,0.16)" : "rgba(61,220,151,0.22)",

    pillWarnBg: isLight ? "rgba(255,184,77,0.10)" : "rgba(245,185,66,0.14)",
    pillWarnText: isLight ? "#b26a00" : "#ffd47b",
    pillWarnBorder: isLight ? "rgba(255,184,77,0.16)" : "rgba(245,185,66,0.22)",

    pillSavingBg: isLight ? "rgba(42,127,255,0.10)" : "rgba(83,167,255,0.14)",
    pillSavingText: isLight ? "#2a7fff" : "#8fc4ff",
    pillSavingBorder: isLight ? "rgba(42,127,255,0.16)" : "rgba(83,167,255,0.22)",

    pillErrBg: isLight ? "rgba(255,107,107,0.10)" : "rgba(255,107,107,0.12)",
    pillErrText: isLight ? "#d64545" : "#ff9a9a",
    pillErrBorder: isLight ? "rgba(255,107,107,0.16)" : "rgba(255,107,107,0.2)",

    cardBg: isLight
      ? "linear-gradient(180deg, #ffffff, #f8fbff)"
      : "linear-gradient(180deg, rgba(15,32,58,0.98), rgba(10,24,43,0.98))",
    cardBorder: isLight ? "rgba(42,127,255,0.10)" : "rgba(123,186,255,0.22)",
    cardShadow: isLight
      ? "0 10px 28px rgba(22,47,79,0.08), inset 0 1px 0 rgba(255,255,255,0.95)"
      : "0 18px 34px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(91,160,255,0.05)",
    cardHoverBorder: isLight ? "rgba(42,127,255,0.18)" : "rgba(109,183,255,0.28)",
    cardHoverShadow: isLight
      ? "0 16px 32px rgba(22,47,79,0.10), 0 0 16px rgba(42,127,255,0.08)"
      : "0 18px 36px rgba(0,0,0,0.34), 0 0 22px rgba(47,128,237,0.12), inset 0 1px 0 rgba(255,255,255,0.04)",
    cardActiveBorder: isLight ? "rgba(42,127,255,0.22)" : "rgba(110,186,255,0.32)",
    cardActiveShadow: isLight
      ? "0 18px 36px rgba(22,47,79,0.12), 0 0 20px rgba(42,127,255,0.10)"
      : "0 20px 40px rgba(0,0,0,0.38), 0 0 28px rgba(47,128,237,0.16), inset 0 1px 0 rgba(255,255,255,0.05)",

    headerBg: isLight
      ? "linear-gradient(180deg, #fdfefe, #f4f9ff)"
      : "linear-gradient(180deg, rgba(16,31,56,0.97), rgba(9,20,38,0.95))",
    headerActiveBg: isLight
      ? "linear-gradient(180deg, #ffffff, #eef6ff)"
      : "linear-gradient(180deg, rgba(20,40,73,0.98), rgba(10,23,43,0.96))",
    headerBorder: isLight ? "rgba(42,127,255,0.08)" : "rgba(111,168,255,0.10)",
    secTitle: isLight ? "#12304c" : "#f4f8ff",
    stampBg: isLight ? "rgba(42,127,255,0.03)" : "rgba(255,255,255,0.015)",
    stamp: isLight ? "#5f738c" : "#a9bdd3",
    stampEmpty: isLight ? "#8a9aab" : "#70839b",

    bodyBg: isLight
      ? "linear-gradient(180deg, rgba(245,249,255,0.92), rgba(240,245,251,0.98))"
      : "linear-gradient(180deg, rgba(9,24,45,0.90), rgba(5,14,28,0.97))",

    groupBg: isLight
      ? "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(245,249,255,0.98))"
      : "linear-gradient(180deg, rgba(17,37,66,0.88), rgba(10,24,43,0.96))",
    groupBorder: isLight ? "rgba(42,127,255,0.08)" : "rgba(123,186,255,0.16)",
    groupShadow: isLight
      ? "inset 0 1px 0 rgba(255,255,255,0.95), 0 6px 16px rgba(20,42,70,0.06)"
      : "inset 0 1px 0 rgba(255,255,255,0.03), 0 10px 22px rgba(0,0,0,0.18), 0 0 0 1px rgba(89,154,255,0.04)",
    groupHeaderBg: isLight
      ? "linear-gradient(180deg, #f7fbff, #eef5ff)"
      : "linear-gradient(180deg, rgba(24,48,82,0.98), rgba(15,34,60,0.97))",
    groupHeaderBorder: isLight ? "rgba(42,127,255,0.08)" : "rgba(123,186,255,0.14)",
    groupTitle: isLight ? "#2a7fff" : "#61b5ff",
    groupTitleMuted: isLight ? "#6883a0" : "#7aa8d9",
    groupSaved: isLight ? "#14995a" : "#74f0ba",
    groupUnsaved: isLight ? "#b26a00" : "#ffd27a",

    progressTrack: isLight ? "rgba(42,127,255,0.10)" : "rgba(125,184,255,0.10)",
    progressBar: isLight
      ? "linear-gradient(90deg,#2a7fff,#6bb8ff)"
      : "linear-gradient(90deg,#3f8fff,#74d7ff)",
    progressText: isLight ? "#5e7794" : "#9db3cc",

    rowBg: isLight
      ? "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(247,250,255,1))"
      : "linear-gradient(180deg, rgba(16,34,58,0.98), rgba(11,26,45,0.98))",
    rowBorder: isLight ? "rgba(42,127,255,0.08)" : "rgba(123,186,255,0.14)",
    rowShadow: isLight
      ? "inset 0 1px 0 rgba(255,255,255,0.95), 0 4px 10px rgba(20,42,70,0.05)"
      : "inset 0 1px 0 rgba(255,255,255,0.035), 0 8px 18px rgba(0,0,0,0.16), 0 0 0 1px rgba(88,154,255,0.03)",
    rowHoverBg: isLight
      ? "linear-gradient(180deg, rgba(248,251,255,1), rgba(239,246,255,1))"
      : "linear-gradient(180deg, rgba(22,45,77,0.99), rgba(14,31,54,1))",
    rowHoverBorder: isLight ? "rgba(42,127,255,0.14)" : "rgba(132,194,255,0.22)",
    rowHoverShadow: isLight
      ? "0 8px 18px rgba(20,42,70,0.08), 0 0 12px rgba(42,127,255,0.06)"
      : "inset 0 1px 0 rgba(255,255,255,0.02), 0 10px 20px rgba(0,0,0,0.16), 0 0 16px rgba(47,128,237,0.08)",

    fieldLabel: isLight ? "#17314b" : "#e7eef8",
    divider: isLight ? "rgba(42,127,255,0.08)" : "rgba(123,186,255,0.14)",

    inputBg: isLight
      ? "linear-gradient(180deg, #ffffff, #f7fbff)"
      : "linear-gradient(180deg, rgba(18,34,56,0.96), rgba(10,21,38,0.96))",
    inputBorder: isLight ? "rgba(42,127,255,0.12)" : "rgba(111,168,255,0.18)",
    inputText: isLight ? "#17314b" : "#eef5ff",

    checkboxBorder: isLight ? "rgba(42,127,255,0.20)" : "rgba(111,168,255,0.20)",
    checkboxGlow: isLight
      ? "0 0 10px rgba(42,127,255,0.08)"
      : "0 0 10px rgba(77,162,255,0.06)",

    pickBtnBg: isLight
      ? "linear-gradient(180deg, #ffffff, #f1f7ff)"
      : "linear-gradient(180deg, rgba(14,30,50,0.96), rgba(8,19,34,0.96))",
    pickBtnText: isLight ? "#1c4672" : "#eaf3ff",
    pickBtnBorder: isLight ? "rgba(42,127,255,0.12)" : "rgba(111,168,255,0.18)",
    pickBtnHoverBg: isLight
      ? "linear-gradient(180deg, #fafdff, #e8f3ff)"
      : "linear-gradient(180deg, rgba(18,39,66,0.98), rgba(10,23,41,0.98))",
    pickBtnHoverBorder: isLight ? "rgba(42,127,255,0.20)" : "rgba(121,189,255,0.28)",
    pickBtnHoverShadow: isLight
      ? "0 10px 20px rgba(28,58,92,0.08), 0 0 14px rgba(42,127,255,0.10)"
      : "0 12px 24px rgba(0,0,0,0.24), 0 0 18px rgba(77,162,255,0.14)",

    pickBtnAltBg: isLight
      ? "linear-gradient(180deg, #f7fbff, #eef5ff)"
      : "linear-gradient(180deg, rgba(24,40,64,0.96), rgba(12,24,42,0.96))",
    pickBtnAltText: isLight ? "#315a84" : "#dce9fb",
    pickBtnAltHoverBg: isLight
      ? "linear-gradient(180deg, #f2f8ff, #e6f1ff)"
      : "linear-gradient(180deg, rgba(30,49,76,0.98), rgba(15,28,48,0.98))",

    photoHint: isLight ? "#7a8da3" : "#8ea3bc",

    thumbBg: isLight ? "#ffffff" : "rgba(11,21,36,0.96)",
    thumbBorder: isLight ? "rgba(42,127,255,0.10)" : "rgba(111,168,255,0.14)",
    thumbPlaceholder: isLight ? "#f2f7fd" : "rgba(20,33,54,0.9)",
    thumbName: isLight ? "#4f6b88" : "#b9cae0",

    submitNote: isLight ? "#6b829d" : "#8ea3bc",
    backBg: isLight
      ? "linear-gradient(90deg,#2a7fff,#1f63d9)"
      : "linear-gradient(90deg,#1f67ff,#124fd1)",
    backShadow: isLight
      ? "0 12px 24px rgba(31,99,217,0.20)"
      : "0 14px 28px rgba(20,79,209,0.28)",
    backHoverShadow: isLight
      ? "0 16px 28px rgba(31,99,217,0.24), 0 0 18px rgba(42,127,255,0.14)"
      : "0 18px 34px rgba(20,79,209,0.34), 0 0 24px rgba(55,125,255,0.22)",

    modalBg: isLight
      ? "linear-gradient(180deg, #ffffff, #f6faff)"
      : "linear-gradient(180deg, rgba(12,22,39,0.98), rgba(8,16,28,0.98))",
    modalBorder: isLight ? "rgba(42,127,255,0.10)" : "rgba(111,168,255,0.16)",
    modalText: isLight ? "#17314b" : "#eef5ff",
    modalMsg: isLight ? "#617791" : "#b6c7da",
    modalCancelBg: isLight ? "#f2f7fd" : "rgba(20,33,54,0.94)",
    modalCancelText: isLight ? "#315a84" : "#e6eef8",
  };

  return {
    page: {
      width: "100%",
      maxWidth: "100%",
      margin: 0,
      padding: "14px 12px 26px",
      boxSizing: "border-box",
      overflowX: "hidden",
      background: "transparent",
      color: c.pageText,
    },

    surface: {
      width: "100%",
      borderRadius: 30,
      padding: "14px 10px 22px",
      background: c.shellBg,
      border: `1px solid ${c.shellBorder}`,
      boxShadow: c.shellShadow,
      overflow: "hidden",
      position: "relative",
    },

    hero: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: "22px 16px 18px",
      marginBottom: 14,
      borderRadius: 24,
      background: c.heroBg,
      border: `1px solid ${c.heroBorder}`,
      boxShadow: c.heroShadow,
      overflow: "hidden",
    },

    heroGlow: {
      position: "absolute",
      left: "50%",
      bottom: -28,
      transform: "translateX(-50%)",
      width: 320,
      height: 130,
      background: isLight
        ? "radial-gradient(circle, rgba(42,127,255,0.12), transparent 68%)"
        : "radial-gradient(circle, rgba(52,145,255,0.28), transparent 68%)",
      filter: "blur(12px)",
      pointerEvents: "none",
    },

    heroBadge: {
      position: "relative",
      zIndex: 1,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "7px 14px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: c.heroBadgeText,
      background: c.heroBadgeBg,
      border: `1px solid ${c.heroBadgeBorder}`,
      boxShadow: isLight
        ? "0 0 18px rgba(42,127,255,0.08)"
        : "0 0 24px rgba(47,128,237,0.12)",
      marginBottom: 16,
    },

    title: {
      position: "relative",
      zIndex: 1,
      margin: 0,
      display: "flex",
      flexWrap: "wrap",
      alignItems: "baseline",
      justifyContent: "center",
      gap: 14,
      lineHeight: 1.03,
      textAlign: "center",
    },

    titleMain: {
      fontSize: "clamp(2rem, 5vw, 3.35rem)",
      fontWeight: 900,
      letterSpacing: "-0.045em",
      color: c.titleMain,
      textShadow: isLight ? "none" : "0 2px 18px rgba(0,0,0,0.24)",
    },

    titleStore: {
      fontSize: "clamp(1.72rem, 4.35vw, 2.95rem)",
      fontWeight: 900,
      letterSpacing: "-0.04em",
      color: c.titleStore,
      textShadow: isLight ? "none" : "0 0 22px rgba(83,167,255,0.30)",
      marginLeft: 10,
    },

    welcome: {
      position: "relative",
      zIndex: 1,
      textAlign: "center",
      fontSize: 16,
      marginTop: 10,
      marginBottom: 0,
      color: c.welcome,
      fontWeight: 500,
    },

    topbar: {
      display: "flex",
      gap: 10,
      alignItems: "center",
      marginBottom: 10,
    },

    topBtn: {
      padding: "8px 12px",
      borderRadius: 12,
      border: `1px solid ${c.pickBtnBorder}`,
      background: c.pickBtnBg,
      color: c.pickBtnText,
      cursor: "pointer",
      boxShadow: isLight ? "0 4px 12px rgba(22,47,79,0.06)" : "0 6px 16px rgba(0,0,0,0.22)",
      transition: "all 180ms ease",
    },

    pill: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "10px 16px",
      borderRadius: 999,
      fontSize: 13,
      margin: "4px auto 18px",
      width: "fit-content",
      maxWidth: "100%",
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: "transparent",
      fontWeight: 700,
      boxShadow: isLight ? "0 6px 14px rgba(22,47,79,0.06)" : "0 8px 18px rgba(0,0,0,0.18)",
      textAlign: "center",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
    },

    pillOk: {
      background: c.pillOkBg,
      color: c.pillOkText,
      borderColor: c.pillOkBorder,
    },

    pillWarn: {
      background: c.pillWarnBg,
      color: c.pillWarnText,
      borderColor: c.pillWarnBorder,
    },

    pillSaving: {
      background: c.pillSavingBg,
      color: c.pillSavingText,
      borderColor: c.pillSavingBorder,
    },

    pillErr: {
      background: c.pillErrBg,
      color: c.pillErrText,
      borderColor: c.pillErrBorder,
    },

    container: {
      display: "flex",
      flexDirection: "column",
      gap: 16,
    },

    empty: {
      textAlign: "center",
      color: c.progressText,
      padding: "40px 0",
    },

    card: {
      borderRadius: 22,
      overflow: "hidden",
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: c.cardBorder,
      background: c.cardBg,
      boxShadow: c.cardShadow,
      marginBottom: 2,
      transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
    },

    cardHover: {
      borderColor: c.cardHoverBorder,
      boxShadow: c.cardHoverShadow,
    },

    cardActive: {
      borderColor: c.cardActiveBorder,
      boxShadow: c.cardActiveShadow,
    },

    header: {
      padding: "18px 16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      background: c.headerBg,
      cursor: "pointer",
      borderBottom: `1px solid ${c.headerBorder}`,
      flexWrap: "wrap",
      transition: "all 180ms ease",
    },

    headerActive: {
      background: c.headerActiveBg,
      boxShadow: isLight
        ? "inset 0 -1px 0 rgba(42,127,255,0.06)"
        : "inset 0 -1px 0 rgba(111,168,255,0.12), 0 0 18px rgba(47,128,237,0.08)",
    },

    headerRight: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
      justifyContent: "flex-end",
      minWidth: 0,
      marginLeft: "auto",
    },

    secTitle: {
      margin: 0,
      fontSize: 22,
      fontWeight: 900,
      color: c.secTitle,
      letterSpacing: "-0.03em",
      textShadow: isLight ? "none" : "0 1px 10px rgba(0,0,0,0.18)",
    },

    stampRow: {
      padding: "12px 16px",
      borderTop: `1px solid ${c.headerBorder}`,
      background: c.stampBg,
    },

    stamp: {
      fontSize: 13,
      color: c.stamp,
    },

    stampEmpty: {
      fontSize: 13,
      color: c.stampEmpty,
      fontStyle: "italic",
    },

    body: {
      padding: "10px 12px 4px",
      background: c.bodyBg,
      display: "flex",
      flexDirection: "column",
      gap: 14,
    },

    groupBox: {
      borderTopWidth: 1,
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderLeftWidth: 1,
      borderStyle: "solid",
      borderColor: c.groupBorder,
      marginTop: 0,
      marginBottom: 6,
      paddingBottom: 8,
      background: c.groupBg,
      borderRadius: 18,
      overflow: "hidden",
      boxShadow: c.groupShadow,
    },

    groupHeader: {
      background: c.groupHeaderBg,
      padding: "16px 16px 14px",
      borderBottom: `1px solid ${c.groupHeaderBorder}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      boxShadow: isLight
        ? "inset 0 -1px 0 rgba(42,127,255,0.04)"
        : "inset 0 -1px 0 rgba(111,168,255,0.08), 0 0 18px rgba(47,128,237,0.05)",
    },

    groupHeaderLeft: {
      display: "flex",
      flexDirection: "column",
      gap: 5,
      minWidth: 0,
      flex: "1 1 200px",
    },

    groupTitle: {
      fontWeight: 900,
      color: c.groupTitle,
      letterSpacing: ".01em",
      fontSize: 18,
      textShadow: isLight ? "none" : "0 0 16px rgba(64,152,255,0.16)",
    },

    groupTitleMuted: {
      fontWeight: 800,
      color: c.groupTitleMuted,
      letterSpacing: ".01em",
      opacity: 0.92,
      fontSize: 16,
    },

    groupSavedMeta: {
      fontSize: 12,
      color: c.groupSaved,
    },

    groupUnsavedMeta: {
      fontSize: 12,
      color: c.groupUnsaved,
      opacity: 0.98,
    },

    groupProgress: {
      fontSize: 12,
      color: c.progressText,
      whiteSpace: "nowrap",
      fontWeight: 700,
    },

    groupProgressBarOuter: {
      height: 7,
      background: c.progressTrack,
      borderRadius: 999,
      overflow: "hidden",
      margin: "0 16px 10px",
    },

    groupProgressBarInner: {
      height: "100%",
      background: c.progressBar,
      width: "0%",
      boxShadow: isLight ? "none" : "0 0 16px rgba(63,143,255,0.35)",
      borderRadius: 999,
    },

    progressText: {
      color: c.progressText,
      fontSize: 12,
      whiteSpace: "nowrap",
      fontWeight: 700,
    },

    progressOuterSm: {
      height: 8,
      width: 126,
      maxWidth: "100%",
      background: c.progressTrack,
      borderRadius: 999,
      overflow: "hidden",
      boxShadow: isLight ? "none" : "inset 0 0 0 1px rgba(125,184,255,0.06)",
    },

    progressInnerSm: {
      height: "100%",
      background: c.progressBar,
      width: "0%",
      borderRadius: 999,
      boxShadow: isLight ? "none" : "0 0 14px rgba(63,143,255,0.32)",
    },

    rows: {
      display: "grid",
      gap: 10,
      padding: "10px 10px 4px",
    },

    row: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 0.9fr)",
      gap: 16,
      padding: "16px",
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: c.rowBorder,
      borderRadius: 16,
      alignItems: "center",
      background: c.rowBg,
      boxShadow: c.rowShadow,
      transition: "background 160ms ease, border-color 160ms ease, box-shadow 160ms ease",
    },

    rowHover: {
      background: c.rowHoverBg,
      borderColor: c.rowHoverBorder,
      boxShadow: c.rowHoverShadow,
    },

    cellLabel: {
      display: "flex",
      alignItems: "center",
      minWidth: 0,
    },

    fieldLabel: {
      fontWeight: 700,
      color: c.fieldLabel,
      fontSize: 15,
      lineHeight: 1.3,
      wordBreak: "break-word",
    },

    cellInput: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      minWidth: 0,
      width: "100%",
      paddingLeft: 10,
      borderLeft: `1px solid ${c.divider}`,
    },

    input: {
      padding: "12px 14px",
      fontSize: 16,
      borderRadius: 14,
      border: `1px solid ${c.inputBorder}`,
      outline: "none",
      width: "100%",
      maxWidth: 280,
      color: c.inputText,
      background: c.inputBg,
      boxShadow: isLight
        ? "0 1px 2px rgba(22,47,79,0.04)"
        : "inset 0 1px 0 rgba(255,255,255,0.03), 0 0 0 1px rgba(77,162,255,0.04)",
      transition: "all 180ms ease",
    },

    checkbox: {
      width: 24,
      height: 24,
      accentColor: "#4da2ff",
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: c.checkboxBorder,
      boxShadow: c.checkboxGlow,
      filter: "drop-shadow(0 0 6px rgba(77,162,255,0.18))",
      transition: "all 160ms ease",
    },

    checkboxHover: {
      borderColor: isLight ? "rgba(42,127,255,0.28)" : "rgba(125,190,255,0.30)",
      boxShadow: isLight ? "0 0 12px rgba(42,127,255,0.12)" : "0 0 14px rgba(77,162,255,0.18)",
    },

    fileInput: {
      padding: 6,
    },

    hiddenFile: {
      position: "absolute",
      opacity: 0,
      width: 1,
      height: 1,
      pointerEvents: "none",
    },

    pickBtn: {
      position: "relative",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      padding: "11px 14px",
      fontSize: 12,
      fontWeight: 800,
      borderRadius: 12,
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: c.pickBtnBorder,
      background: c.pickBtnBg,
      color: c.pickBtnText,
      cursor: "pointer",
      userSelect: "none",
      whiteSpace: "nowrap",
      boxShadow: isLight ? "0 4px 10px rgba(22,47,79,0.06)" : "0 8px 18px rgba(0,0,0,0.2)",
      transition: "all 180ms ease",
    },

    pickBtnHover: {
      transform: "translateY(-1px)",
      borderColor: c.pickBtnHoverBorder,
      background: c.pickBtnHoverBg,
      boxShadow: c.pickBtnHoverShadow,
    },

    pickBtnAlt: {
      position: "relative",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      padding: "11px 14px",
      fontSize: 12,
      fontWeight: 800,
      borderRadius: 12,
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: c.pickBtnBorder,
      background: c.pickBtnAltBg,
      color: c.pickBtnAltText,
      cursor: "pointer",
      userSelect: "none",
      whiteSpace: "nowrap",
      boxShadow: isLight ? "0 4px 10px rgba(22,47,79,0.05)" : "0 8px 18px rgba(0,0,0,0.2)",
      transition: "all 180ms ease",
    },

    pickBtnAltHover: {
      transform: "translateY(-1px)",
      borderColor: c.pickBtnHoverBorder,
      background: c.pickBtnAltHoverBg,
      boxShadow: isLight
        ? "0 10px 20px rgba(22,47,79,0.07), 0 0 12px rgba(42,127,255,0.08)"
        : "0 12px 24px rgba(0,0,0,0.24), 0 0 18px rgba(77,162,255,0.12)",
    },

    photoHint: {
      fontSize: 12,
      opacity: 0.92,
      marginTop: 4,
      color: c.photoHint,
      textAlign: "right",
      width: "100%",
    },

    photoUploadStatus: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "9px 12px",
      borderRadius: 999,
      border: `1px solid ${isLight ? "rgba(42,127,255,0.18)" : "rgba(111,168,255,0.22)"}`,
      background: isLight
        ? "linear-gradient(180deg, rgba(232,244,255,0.96), rgba(244,249,255,0.96))"
        : "linear-gradient(180deg, rgba(24,48,82,0.96), rgba(14,31,54,0.96))",
      color: isLight ? "#2a7fff" : "#8fc4ff",
      fontSize: 12,
      fontWeight: 800,
      boxShadow: isLight ? "0 6px 14px rgba(22,47,79,0.06)" : "0 10px 22px rgba(0,0,0,0.18)",
    },
    photoHeaderRow: {
      display: "grid",
      gridTemplateColumns: "1fr auto",
      alignItems: "start",
      gap: 10,
      width: "100%",
    },

    photoStatusSlot: {
      minHeight: 36,
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      minWidth: 0,
    },

    photoActionSlot: {
      display: "flex",
      gap: 8,
      alignItems: "flex-start",
      justifyContent: "flex-end",
      flexWrap: "wrap",
      minWidth: 0,
    },

    photoClearSlot: {
      minWidth: 72,
      display: "flex",
      justifyContent: "flex-end",
    },


    photoUploadSpinner: {
      width: 14,
      height: 14,
      borderRadius: "50%",
      border: `2px solid ${isLight ? "rgba(42,127,255,0.24)" : "rgba(143,196,255,0.24)"}`,
      borderTopColor: isLight ? "#2a7fff" : "#8fc4ff",
      animation: "neoSpin 0.9s linear infinite",
      display: "inline-block",
      flex: "0 0 auto",
    },

    clearBtn: {
      marginLeft: 0,
      padding: "9px 12px",
      fontSize: 12,
      fontWeight: 700,
      borderRadius: 10,
      border: `1px solid ${c.pickBtnBorder}`,
      background: c.pickBtnAltBg,
      color: c.pickBtnAltText,
      cursor: "pointer",
      whiteSpace: "nowrap",
      transition: "all 180ms ease",
    },

    thumbWrap: {
      display: "flex",
      flexWrap: "wrap",
      gap: 10,
      justifyContent: "flex-end",
      width: "100%",
      maxWidth: "100%",
    },

    thumb: {
      position: "relative",
      width: 112,
      maxWidth: "100%",
      border: `1px solid ${c.thumbBorder}`,
      borderRadius: 14,
      overflow: "hidden",
      background: c.thumbBg,
      boxShadow: isLight ? "0 6px 14px rgba(22,47,79,0.06)" : "0 10px 22px rgba(0,0,0,0.24)",
    },

    thumbImg: {
      width: "100%",
      height: 88,
      objectFit: "cover",
      display: "block",
      background: c.thumbPlaceholder,
    },

    thumbUploadingOverlay: {
      position: "absolute",
      inset: 0,
      background: isLight ? "rgba(255,255,255,0.68)" : "rgba(4,12,24,0.62)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      zIndex: 2,
      backdropFilter: "blur(2px)",
      WebkitBackdropFilter: "blur(2px)",
    },

    thumbUploadingText: {
      fontSize: 12,
      fontWeight: 800,
      color: isLight ? "#1d4f8f" : "#eaf3ff",
    },

    thumbPlaceholder: {
      width: "100%",
      height: 88,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: c.thumbPlaceholder,
      color: c.photoHint,
      fontSize: 12,
    },

    thumbFooter: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 6,
      padding: "7px 8px",
    },

    thumbName: {
      fontSize: 11,
      color: c.thumbName,
    },

    thumbDel: {
      border: `1px solid ${c.thumbBorder}`,
      background: c.pickBtnAltBg,
      color: c.pickBtnAltText,
      borderRadius: 8,
      cursor: "pointer",
      width: 26,
      height: 24,
      lineHeight: "22px",
      textAlign: "center",
      transition: "all 180ms ease",
    },

    progressOuter: {
      height: 6,
      background: c.progressTrack,
      width: "100%",
    },

    progressInner: {
      height: 6,
      background: c.progressBar,
      boxShadow: isLight ? "none" : "0 0 12px rgba(63,143,255,0.28)",
    },

    actionsRow: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 8,
      padding: "14px 16px 18px",
    },

    actionBtn: {
      padding: "12px 18px",
      fontSize: 15,
      borderRadius: 14,
      border: "none",
      cursor: "pointer",
      fontWeight: 800,
      transition: "all 180ms ease",
    },

    saveBtn: {
      background: isLight
        ? "linear-gradient(180deg,#2a7fff,#1f63d9)"
        : "linear-gradient(180deg,#2d8cff,#1f6fe0)",
      color: "#fff",
      boxShadow: isLight
        ? "0 10px 20px rgba(31,99,217,0.18)"
        : "0 12px 24px rgba(47,128,237,0.24)",
    },

    submitBtn: {
      background: isLight
        ? "linear-gradient(180deg,#20b76d,#14995a)"
        : "linear-gradient(180deg,#29c977,#159a59)",
      color: "#fff",
      boxShadow: isLight
        ? "0 10px 20px rgba(20,153,90,0.18)"
        : "0 12px 24px rgba(31,157,85,0.24)",
    },

    submitNote: {
      marginTop: 8,
      fontSize: 12,
      color: c.submitNote,
      textAlign: "right",
    },

    submitAsNote: {
      fontSize: 12,
      color: c.progressText,
      textAlign: "right",
      fontWeight: 700,
    },

    backBtn: {
      marginTop: 6,
      width: "100%",
      border: `1px solid ${isLight ? "rgba(42,127,255,0.16)" : "rgba(111,168,255,0.22)"}`,
      borderRadius: 18,
      padding: "15px 16px",
      fontSize: 16,
      fontWeight: 800,
      cursor: "pointer",
      background: c.backBg,
      color: "#fff",
      boxShadow: c.backShadow,
      transition: "all 180ms ease",
    },

    backBtnHover: {
      transform: "translateY(-1px)",
      filter: "brightness(1.06)",
      boxShadow: c.backHoverShadow,
    },

    toast: {
      position: "fixed",
      left: "50%",
      transform: "translateX(-50%)",
      bottom: 24,
      color: "#fff",
      padding: "10px 14px",
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      gap: 12,
      boxShadow: "0 18px 38px rgba(0,0,0,0.28)",
      zIndex: 9999,
    },

    toastX: {
      background: "transparent",
      border: "none",
      color: "#fff",
      fontSize: 18,
      cursor: "pointer",
      lineHeight: 1,
    },

    modalOverlay: {
      position: "fixed",
      inset: 0,
      background: isLight ? "rgba(20,42,70,0.18)" : "rgba(2,6,23,0.62)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9998,
      padding: 16,
    },

    modal: {
      width: "min(520px, 92vw)",
      background: c.modalBg,
      borderRadius: 20,
      border: `1px solid ${c.modalBorder}`,
      boxShadow: isLight ? "0 18px 34px rgba(20,42,70,0.12)" : "0 22px 50px rgba(0,0,0,0.42)",
      padding: 18,
      color: c.modalText,
    },

    modalTitle: {
      fontSize: 18,
      fontWeight: 800,
      marginBottom: 8,
    },

    modalMsg: {
      fontSize: 14,
      color: c.modalMsg,
      marginBottom: 14,
    },

    modalActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 10,
      flexWrap: "wrap",
    },

    modalBtn: {
      padding: "10px 14px",
      borderRadius: 12,
      border: "none",
      cursor: "pointer",
      fontSize: 14,
      fontWeight: 700,
    },

    modalCancel: {
      background: c.modalCancelBg,
      color: c.modalCancelText,
      border: `1px solid ${c.modalBorder}`,
    },

    modalConfirm: {
      background: "#ff6b6b",
      color: "#fff",
    },
    binaryWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 10,
      flexWrap: "wrap",
      width: "100%",
    },

    binaryBtn: {
      minWidth: 108,
      padding: "12px 16px",
      borderRadius: 14,
      border: `1px solid ${c.pickBtnBorder}`,
      background: c.pickBtnBg,
      color: c.pickBtnText,
      fontWeight: 800,
      cursor: "pointer",
      boxShadow: isLight ? "0 6px 12px rgba(22,47,79,0.05)" : "0 10px 20px rgba(0,0,0,0.18)",
      transition: "all 180ms ease",
    },

    binaryBtnYesActive: {
      background: isLight
        ? "linear-gradient(180deg, #e8fff2, #dff8eb)"
        : "linear-gradient(180deg, rgba(23, 201, 110, 0.28), rgba(15, 122, 74, 0.34))",
      color: isLight ? "#11834d" : "#8ff0bf",
      border: "1px solid rgba(23, 201, 110, 0.34)",
      boxShadow: isLight
        ? "0 10px 18px rgba(20,153,90,0.10)"
        : "0 10px 22px rgba(23,201,110,0.14)",
    },

    binaryBtnNoActive: {
      background: isLight
        ? "linear-gradient(180deg, #fff1f1, #ffe7e7)"
        : "linear-gradient(180deg, rgba(255, 107, 107, 0.22), rgba(122, 33, 33, 0.30))",
      color: isLight ? "#c54040" : "#ffb3b3",
      border: "1px solid rgba(255, 107, 107, 0.30)",
      boxShadow: isLight
        ? "0 10px 18px rgba(197,64,64,0.08)"
        : "0 10px 22px rgba(255,107,107,0.12)",
    },

    customPanel: {
      display: "grid",
      gap: 16,
    },

    customToolbar: {
      display: "grid",
      gap: 12,
      padding: "14px",
      borderRadius: 18,
      background: c.groupBg,
      border: `1px solid ${c.groupBorder}`,
      boxShadow: c.groupShadow,
    },

    customToolbarBtns: {
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
    },

    smallGhostBtn: {
      padding: "10px 12px",
      borderRadius: 12,
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: c.pickBtnBorder,
      background: c.pickBtnAltBg,
      color: c.pickBtnAltText,
      fontWeight: 800,
      cursor: "pointer",
      boxShadow: isLight ? "0 4px 10px rgba(22,47,79,0.05)" : "0 8px 16px rgba(0,0,0,0.16)",
    },

    emptyInline: {
      fontSize: 13,
      color: c.progressText,
      padding: "4px 2px",
    },

    customItems: {
      display: "grid",
      gap: 12,
    },

    customItemCard: {
      border: `1px solid ${c.groupBorder}`,
      borderRadius: 18,
      background: c.groupBg,
      boxShadow: c.groupShadow,
      padding: 14,
      display: "grid",
      gap: 10,
    },

    customItemTop: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
    },

    customItemMeta: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
    },

    customItemIndex: {
      width: 28,
      height: 28,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 999,
      background: c.pickBtnAltBg,
      color: c.pickBtnAltText,
      border: `1px solid ${c.pickBtnBorder}`,
      fontWeight: 900,
      fontSize: 12,
    },

    customItemType: {
      fontSize: 12,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: c.progressText,
    },

    removeMiniBtn: {
      padding: "8px 10px",
      borderRadius: 10,
      border: `1px solid ${c.pickBtnBorder}`,
      background: c.pickBtnAltBg,
      color: c.pickBtnAltText,
      fontWeight: 700,
      cursor: "pointer",
    },

  };
};
